/**
 * Sprint 4A.1 — Refinement scheda vendita.
 * Componente embeddable: rende la scheda vendita 2 colonne.
 *
 * Esportato come default export `VenditaDetailContent`. Riceve `venditaId`
 * come prop. Se manca/è null mostra un placeholder vuoto.
 *
 * Refinement principali:
 * - Indicatore margine spostato in header (pallino + scritta, no sfondo)
 * - 4 macro-sezioni con bande separatrici: Importi · Polizze e finanziamenti · Pagamenti · Note
 * - Box residuo colorato (rosso/verde/blu)
 * - Sezione Veicolo con campo Acquisto (readonly) + Vendita (editabile)
 * - Hardening errori del salvataggio righe (mostra dettaglio backend)
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import AvvisoPagina from "@/components/AvvisoPagina";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { NomeVenditore } from "@/components/NomeVenditore";
import { haFlag, ruoloGestione } from "@/lib/ambito";
import { ConfrontoRegime } from "@/components/vendite/RiallineamentoRegime";
import { VEHICLE_TIPO_CARICO_LABELS } from "@/lib/labels";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import CodiceIvaPicker from "@/components/CodiceIvaPicker";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Pencil, Trash2, Plus, Car, CreditCard, ArrowLeftRight,
  Landmark, Banknote, StickyNote,
  Eye, EyeOff, Lock as LockIcon, Unlock, CheckCircle2, AlertTriangle,
  FileText, FileSignature, Copy, RotateCcw, Calendar as CalendarIcon,
} from "lucide-react";
import { formatEUR, formatEURMasked } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import CambioDisponibilitaModal from "@/components/CambioDisponibilitaModal";
import VehicleStatusBadge from "@/components/VehicleStatusBadge";
import TipoCaricoBadge from "@/components/TipoCaricoBadge";
import GaranzieSection from "@/components/GaranzieSection";
import PolizzaCVTSection from "@/components/PolizzaCVTSection";
import FinanziamentoPopup, { FinanziamentoView } from "@/components/FinanziamentoPopup";
import PassaggioProprietaPopup, { PassaggioProprietaView } from "@/components/PassaggioProprietaPopup";
import AnagraficaPicker from "@/components/AnagraficaPicker";
import { ClienteDaCrm, DifferenzeAnagrafica, RigaOpportunita, collegaContatto, useOpportunitaVendita } from "@/pages/crm/CollegamentiOpportunita";
import RiapriPraticaDialog from "@/components/RiapriPraticaDialog";
import AnagraficaViewModal from "@/components/AnagraficaViewModal";
import VeicoloPicker from "@/components/VeicoloPicker";
import OrigineIvaLabel from "@/components/OrigineIvaLabel";
import Conflitti, { campiModificati, sovrascrittura } from "@/components/Conflitti";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const STATUS_LABELS = {
  draft: "Bozza", impegnata: "Impegnata", venduta: "Venduta",
  consegnata: "Consegnata", annullata: "Annullata",
  in_corso: "In corso", scaduto: "Scaduto", confermato: "Confermato", annullato: "Annullato",
};

// Sprint 4A.6: colori badge status vendita coerenti con veicolo
const STATUS_BADGE_CLASS = {
  draft: "bg-slate-100 text-slate-700",
  impegnata: "bg-amber-100 text-amber-800",
  venduta: "bg-red-100 text-red-800",
  consegnata: "bg-violet-100 text-violet-800",
  annullata: "bg-rose-100 text-rose-800",
};

const TIPI_SCONTO = [
  { value: "venditore", label: "Sconto venditore" },
  { value: "promo", label: "Promo" },
  { value: "rottamazione_governo", label: "Rottamazione governo" },
  { value: "fedelta", label: "Fedeltà" },
  { value: "altro", label: "Altro" },
];

const MODALITA_CAPARRA = [
  { v: "bonifico", l: "Bonifico" }, { v: "contanti", l: "Contanti" },
  { v: "assegno", l: "Assegno" }, { v: "carta", l: "Carta" }, { v: "bancomat", l: "Bancomat" },
];

// -------- helpers --------
function extractError(e) {
  const d = e?.response?.data?.detail;
  if (!d) return "Errore imprevisto";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x.msg || JSON.stringify(x)).join(" · ");
  return JSON.stringify(d);
}

// -------- BANDA SEPARATRICE --------
function BandaSezione({ titolo, children }) {
  // Sprint 4B.2 (B8): compattata
  return (
    <div className="relative my-2.5">
      <div className="border-t-[2px] border-slate-300" />
      <div className="absolute -top-2.5 left-4 px-2.5 py-0.5 bg-white border border-slate-300 rounded-full text-[10px] uppercase tracking-wider font-semibold text-slate-700">
        {titolo}{children}
      </div>
    </div>
  );
}

// Sprint 4F1.2 — Placeholder card per voci future (Sprint 4F2: Passaggio
// proprietà, Sprint 4F3+: Assicurazione CVT esterna). Mostra il "posto"
// senza funzionalità, in grigio.
function PlaceholderCard({ titolo, sprint, "data-testid": testId }) {
  return (
    <Card className="p-3 bg-slate-50/40 border-dashed" data-testid={testId}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-slate-400">
          <span className="text-xs">▶</span>
          <Plus className="h-4 w-4" />
          <h3 className="font-heading text-base font-medium">{titolo}</h3>
          <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-slate-200 text-slate-500 ml-1">
            Coming soon · Sprint {sprint}
          </span>
        </div>
        <Button size="sm" variant="outline" disabled className="opacity-50 cursor-not-allowed">
          <Plus className="h-4 w-4 mr-1" /> Aggiungi
        </Button>
      </div>
    </Card>
  );
}

// -------- Sprint 4B.2 (B8): card collapsible (default-collapsed se vuota) --------
function CardCollapsible({ titolo, titleExtra, icon: Icon, iconColor, populated, summary, actions, children, "data-testid": testId }) {
  const [open, setOpen] = useState(populated);
  useEffect(() => { setOpen(!!populated); }, [populated]);
  return (
    <Card className="p-3" data-testid={testId}>
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="flex items-center gap-2 hover:text-blue-600 transition-colors"
          data-testid={testId ? `${testId}-toggle` : undefined}
          data-open={open ? "true" : "false"}
        >
          <span className={`text-slate-400 text-xs transition-transform ${open ? "rotate-90" : ""}`}>▶</span>
          {Icon && <Icon className={`h-4 w-4 ${iconColor || "text-slate-700"}`} />}
          <h3 className="font-heading text-base font-semibold text-slate-900">{titolo}</h3>
          {titleExtra}
          {!open && summary && (
            <span className="text-[11px] text-slate-500 ml-1">{summary}</span>
          )}
          {!open && !populated && (
            <span className="text-[11px] text-slate-400 italic ml-1">vuoto</span>
          )}
        </button>
        {actions}
      </div>
      {open && <div className="mt-2">{children}</div>}
    </Card>
  );
}

// -------- Popup riga generico --------
function RigaPopup({ open, onOpenChange, categoria, initial, onSave,
                     righeCollegabili, aziendaIvaDefault, contextCompanyId }) {
  const [form, setForm] = useState(initial || {});
  // Sprint 4I1.B2.C — sezione "Avanzato" collassata di default
  const [advancedOpen, setAdvancedOpen] = useState(false);
  // Sprint 4I1.C — tracking origine pre-fill IVA per nuove righe
  const [ivaAcqSource, setIvaAcqSource] = useState(null); // 'azienda'|'fornitore'|'override'
  const [ivaVenSource, setIvaVenSource] = useState(null);
  const [ivaScontoSource, setIvaScontoSource] = useState(null);
  const [fornitoreData, setFornitoreData] = useState(null); // {nome, iva_codice}
  useEffect(() => {
    setForm(initial || {});
    setAdvancedOpen(false);
    // Sprint 4I1.C.1 — Per record esistenti con override DB-side, preserva
    // il source="override" così il save reinvia l'IVA esplicita (no propagation
    // ricalcolo). Altrimenti reset a null (default).
    const hasDbOverride = !!(initial?.iva_codice_override
      || initial?.iva_codice_origin === "override_utente");
    setIvaAcqSource(hasDbOverride ? "override" : null);
    setIvaVenSource(hasDbOverride ? "override" : null);
    setIvaScontoSource(hasDbOverride ? "override" : null);
    setFornitoreData(null);
  }, [initial, open]);

  const isNew = !initial?.id;
  const isSconto = categoria === "sconti";

  // Sprint 4I1.C — Pre-fill IVA al mount per nuove righe (da azienda default).
  // Non sovrascrive valori esistenti su modifica.
  useEffect(() => {
    if (!open || !isNew || !aziendaIvaDefault) return;
    setForm((f) => {
      const u = {};
      if (!isSconto) {
        if (!f.iva_codice_acquisto) u.iva_codice_acquisto = aziendaIvaDefault;
        if (!f.iva_codice_vendita) u.iva_codice_vendita = aziendaIvaDefault;
      } else if (!f.iva_codice) {
        u.iva_codice = aziendaIvaDefault;
      }
      return Object.keys(u).length ? { ...f, ...u } : f;
    });
    if (!isSconto) {
      setIvaAcqSource((s) => s || "azienda");
      setIvaVenSource((s) => s || "azienda");
    } else {
      setIvaScontoSource((s) => s || "azienda");
    }
  }, [open, isNew, isSconto, aziendaIvaDefault]);

  // Sprint 4I1.C — Quando cambia fornitore, fetch anagrafica e propaga IVA
  // sui codici NON ancora "override_utente". Solo righe servizi/accessori.
  const fornitoreId = form?.dettaglio?.fornitore_id || null;
  useEffect(() => {
    if (!open || isSconto) return;
    if (!fornitoreId) {
      setFornitoreData(null);
      return;
    }
    let cancelled = false;
    api.get(`/anagrafiche/${fornitoreId}`).then((r) => {
      if (cancelled) return;
      const a = r.data || {};
      const ivaCod = a.iva_codice || null;
      const nameLabel = a.ragione_sociale
        || `${a.cognome || ""} ${a.nome || ""}`.trim()
        || a.codice || "Fornitore";
      setFornitoreData({ nome: nameLabel, iva_codice: ivaCod });
      if (ivaCod) {
        setForm((f) => {
          const u = {};
          if (ivaAcqSource !== "override") u.iva_codice_acquisto = ivaCod;
          if (ivaVenSource !== "override") u.iva_codice_vendita = ivaCod;
          return Object.keys(u).length ? { ...f, ...u } : f;
        });
        setIvaAcqSource((s) => (s === "override" ? s : "fornitore"));
        setIvaVenSource((s) => (s === "override" ? s : "fornitore"));
      }
    }).catch(() => { /* silent */ });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, fornitoreId, isSconto]);

  const titoloCat = {
    servizi: "Servizio aggiuntivo",
    accessori: "Accessorio",
    sconti: "Sconto",
  }[categoria];

  // Sprint 4I1.B2.C — Flag "iva_da_codificare" (record migrato senza codice)
  const ivaDaCodificare = !!(form?._data_quality?.iva_da_codificare);

  // Sprint 4I1.B2.C — Sconto: se collegato a riga, IVA readonly (ereditata)
  const rigaCollegataId = form?.dettaglio?.riga_collegata_id || null;
  const rigaCollegata = (righeCollegabili || []).find((r) => r.id === rigaCollegataId);

  // Sprint 4I1.B2.C — Badge "origine" del codice IVA
  const origin = form?.iva_codice_origin;
  const isOverride = !!form?.iva_codice_override || origin === "override_utente";
  const originBadge = (() => {
    if (!origin) return null;
    if (origin === "regola_propagazione")
      return { label: "Propagato", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" };
    if (origin === "default_categoria" || origin === "default_azienda" || origin === "ereditato_da_riga")
      return { label: "Default", cls: "bg-amber-50 text-amber-700 border-amber-200" };
    if (origin === "override_utente")
      return { label: "Override", cls: "bg-rose-50 text-rose-700 border-rose-200" };
    return null;
  })();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px]" data-testid={`riga-popup-${categoria}`}>
        <DialogHeader>
          <DialogTitle>{initial?.id ? "Modifica" : "Aggiungi"} — {titoloCat}</DialogTitle>
          <DialogDescription>Compila i dati della riga. Verrà ricalcolato il margine.</DialogDescription>
        </DialogHeader>
        {ivaDaCodificare && (
          <div className="rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900"
               data-testid="riga-iva-da-codificare-banner">
            ⚠️ Codice IVA non ancora codificato per questo record. Espandi <em>Avanzato</em> e seleziona il valore corretto.
          </div>
        )}
        <div className="space-y-3 py-2">
          <div>
            <Label>Descrizione *</Label>
            <Input
              value={form.descrizione || ""}
              onChange={(e) => setForm({ ...form, descrizione: e.target.value })}
              placeholder={isSconto ? "es. Sconto venditore" : "es. Tagliando premium"}
              data-testid="riga-desc"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            {!isSconto && (
              <div>
                <Label className="text-amber-700">Importo acquisto (€)</Label>
                <Input
                  type="number" step="0.01"
                  value={form.importo_acquisto ?? ""}
                  onChange={(e) => setForm({ ...form, importo_acquisto: e.target.value })}
                  className="border-l-4 border-amber-400"
                  data-testid="riga-acq"
                />
              </div>
            )}
            <div className={isSconto ? "col-span-2" : ""}>
              <Label className="text-blue-700">
                Importo vendita (€) {isSconto && <span className="text-rose-600 text-xs ml-1">(usa valori negativi)</span>}
              </Label>
              <Input
                type="number" step="0.01"
                value={form.importo_vendita ?? ""}
                onChange={(e) => setForm({ ...form, importo_vendita: e.target.value })}
                className="border-l-4 border-blue-400"
                data-testid="riga-vend"
              />
            </div>
          </div>
          {categoria === "accessori" && (
            <div>
              <Label>Codice articolo</Label>
              <Input
                value={form.dettaglio?.codice_articolo || ""}
                onChange={(e) => setForm({
                  ...form,
                  dettaglio: { ...(form.dettaglio || {}), codice_articolo: e.target.value },
                })}
              />
            </div>
          )}
          {(categoria === "servizi" || categoria === "accessori") && (
            <div>
              <Label>Fornitore</Label>
              <AnagraficaPicker
                value={form.dettaglio?.fornitore_id || null}
                onChange={(id) => setForm({
                  ...form,
                  dettaglio: { ...(form.dettaglio || {}), fornitore_id: id },
                })}
                contextCompanyId={contextCompanyId || null}
                testIdPrefix={`riga-fornitore-${categoria}`}
              />
            </div>
          )}
          {isSconto && (
            <div>
              <Label>Tipo sconto</Label>
              <Select
                value={form.dettaglio?.tipo_sconto || ""}
                onValueChange={(v) => setForm({
                  ...form,
                  dettaglio: { ...(form.dettaglio || {}), tipo_sconto: v },
                })}
              >
                <SelectTrigger data-testid="riga-tipo-sconto"><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                <SelectContent>
                  {TIPI_SCONTO.map((t) => (<SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
          )}
          {!isSconto && (
            <div className="flex items-center gap-2">
              <Switch
                checked={!!form.nascondi_acquisto}
                onCheckedChange={(v) => setForm({ ...form, nascondi_acquisto: v })}
                data-testid="riga-nascondi"
              />
              <Label className="text-sm">Nascondi prezzo di acquisto al cliente</Label>
            </div>
          )}
          <div>
            <Label>Note</Label>
            <Textarea rows={2} value={form.note || ""} onChange={(e) => setForm({ ...form, note: e.target.value })} />
          </div>

          {/* Sprint 4I1.B2.C — Sezione "Avanzato" collassabile */}
          <div className="border-t pt-2">
            <button type="button"
                    onClick={() => setAdvancedOpen((x) => !x)}
                    className="flex items-center gap-2 text-sm font-medium text-slate-700 hover:text-slate-900"
                    data-testid="riga-avanzato-toggle">
              <span>{advancedOpen ? "▼" : "▶"}</span>
              <span>Avanzato / Amministrativo</span>
              {originBadge && (
                <span className={`ml-2 px-2 py-0.5 rounded text-[10px] border ${originBadge.cls}`}
                      data-testid="riga-iva-origin-badge">
                  {originBadge.label}
                </span>
              )}
            </button>
            {advancedOpen && (
              <div className="mt-3 space-y-3 rounded-md bg-slate-50 border p-3" data-testid="riga-avanzato-content">
                {!isSconto && (
                  <>
                    <div>
                      <Label className="text-xs">Codice IVA acquisto</Label>
                      <CodiceIvaPicker
                        value={form.iva_codice_acquisto}
                        onChange={(v) => { setForm({ ...form, iva_codice_acquisto: v }); setIvaAcqSource("override"); }}
                        testId={`riga-iva-acquisto-${categoria}`}
                        placeholder="Selezionare..."
                      />
                      <OrigineIvaLabel source={ivaAcqSource} fornitoreNome={fornitoreData?.nome}
                                       testId={`riga-iva-acq-origine-${categoria}`} />
                    </div>
                    <div>
                      <Label className="text-xs">Codice IVA vendita</Label>
                      <CodiceIvaPicker
                        value={form.iva_codice_vendita}
                        onChange={(v) => { setForm({ ...form, iva_codice_vendita: v }); setIvaVenSource("override"); }}
                        testId={`riga-iva-vendita-${categoria}`}
                        placeholder="Selezionare..."
                      />
                      <OrigineIvaLabel source={ivaVenSource} fornitoreNome={fornitoreData?.nome}
                                       testId={`riga-iva-ven-origine-${categoria}`} />
                    </div>
                    <p className="text-[11px] text-slate-500">
                      Default da fornitore selezionato. Se fornitore non specificato,
                      usa IVA default azienda ({aziendaIvaDefault || "I22"}). Modificabile per casi specifici.
                    </p>
                  </>
                )}
                {isSconto && (
                  <>
                    <div>
                      <Label className="text-xs">Collegato a riga</Label>
                      <Select
                        value={rigaCollegataId || "__none__"}
                        onValueChange={(val) => {
                          const newDett = { ...(form.dettaglio || {}) };
                          if (val === "__none__") {
                            delete newDett.riga_collegata_id;
                            setForm({ ...form, dettaglio: newDett, iva_codice: aziendaIvaDefault || "I22" });
                            setIvaScontoSource("azienda");
                          } else {
                            newDett.riga_collegata_id = val;
                            const r = (righeCollegabili || []).find((x) => x.id === val);
                            const ivaInh = r?.iva_codice_vendita || aziendaIvaDefault || "I22";
                            setForm({ ...form, dettaglio: newDett, iva_codice: ivaInh });
                            setIvaScontoSource("ereditata");
                          }
                        }}
                      >
                        <SelectTrigger data-testid="riga-sconto-collegata"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">— Nessuno (sconto generico) —</SelectItem>
                          {(righeCollegabili || []).map((r) => (
                            <SelectItem key={r.id} value={r.id}>
                              {r._cat === "servizi" ? "Servizio: " : "Accessorio: "}{r.descrizione}
                              {r.iva_codice_vendita && ` — IVA ${r.iva_codice_vendita}`}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Codice IVA</Label>
                      <CodiceIvaPicker
                        value={form.iva_codice}
                        onChange={(v) => { setForm({ ...form, iva_codice: v }); setIvaScontoSource("override"); }}
                        testId="riga-iva-sconto"
                        placeholder="Selezionare..."
                        disabled={!!rigaCollegataId}
                      />
                      <OrigineIvaLabel source={ivaScontoSource} fornitoreNome={rigaCollegata ? `riga ${rigaCollegata._cat}` : null}
                                       testId="riga-iva-sconto-origine" />
                    </div>
                    <p className="text-[11px] text-slate-500">
                      {rigaCollegata
                        ? <>IVA ereditata dalla riga collegata ({rigaCollegata._cat === "servizi" ? "Servizio" : "Accessorio"}: {rigaCollegata.descrizione}).</>
                        : <>Sconto generico. IVA di default dall'azienda ({aziendaIvaDefault || "I22"}).</>}
                      {isOverride && " — Override manuale attivo."}
                    </p>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button
            onClick={() => {
              if (!form.descrizione?.trim()) { toast.error("Descrizione obbligatoria"); return; }
              const payload = {
                descrizione: form.descrizione.trim(),
                importo_acquisto: parseFloat(form.importo_acquisto || 0) || 0,
                importo_vendita: parseFloat(form.importo_vendita || 0) || 0,
                nascondi_acquisto: !!form.nascondi_acquisto,
                dettaglio: form.dettaglio || {},
                note: form.note || null,
              };
              // Sprint 4I1.C.1 — Invia IVA esplicita SOLO se l'utente l'ha
              // modificata manualmente (source === "override"). Altrimenti
              // omettila e lascia al backend di propagarla dal fornitore.
              if (isSconto) {
                if (ivaScontoSource === "override" && form.iva_codice) {
                  payload.iva_codice = form.iva_codice;
                }
              } else {
                if (ivaAcqSource === "override" && form.iva_codice_acquisto) {
                  payload.iva_codice_acquisto = form.iva_codice_acquisto;
                }
                if (ivaVenSource === "override" && form.iva_codice_vendita) {
                  payload.iva_codice_vendita = form.iva_codice_vendita;
                }
              }
              if (form.id) payload.id = form.id;
              onSave(payload);
            }}
            data-testid="riga-save"
          >
            Salva
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// -------- Popup finanziamento (Sprint 4F1.3: spostato in components/FinanziamentoPopup.jsx) --------

// -------- Popup permuta --------
// Sprint PERMUTA.1 — Popup permuta esteso: campi anagrafici completi +
// possibilità di agganciare un veicolo esistente. La materializzazione
// (creazione veicolo INCOMING in /conferma) è demandata a PERMUTA.2.
function PermutaPopup({ open, onOpenChange, initial, onSave, onAgganciaVehicle }) {
  const [form, setForm] = useState(initial || {});
  useEffect(() => { setForm(initial || {}); }, [initial, open]);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const isAgganciata = !!form.vehicle_id_agganciato;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[760px] max-h-[85vh] overflow-y-auto" data-testid="perm-popup">
        <DialogHeader>
          <DialogTitle>Permuta</DialogTitle>
          <DialogDescription>
            Veicolo che il cliente cede acquistando questa pratica. I campi
            anagrafici saranno usati per materializzare il veicolo alla
            conversione preventivo→contratto (Sprint PERMUTA.2).
          </DialogDescription>
        </DialogHeader>

        {/* Anteprima veicolo agganciato (se presente) */}
        {isAgganciata && (
          <div className="rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-sm flex items-center justify-between"
               data-testid="perm-aggancio-info">
            <span>
              <span className="font-medium">Veicolo agganciato:</span>{" "}
              <span className="font-mono text-xs">{form.vehicle_id_agganciato}</span>
            </span>
            <Button type="button" variant="ghost" size="sm"
              onClick={() => set({ vehicle_id_agganciato: null })}
              data-testid="perm-aggancio-clear">
              <Trash2 className="h-3.5 w-3.5 mr-1" /> Scollega
            </Button>
          </div>
        )}

        {/* Sezione 1: Identificazione */}
        <div className="space-y-3 py-2">
          <div className="text-xs font-semibold uppercase text-slate-500 tracking-wide">Identificazione</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label>Tipo veicolo</Label>
              <Select value={form.categoria_veicolo || ""}
                      onValueChange={(v) => set({ categoria_veicolo: v || null })}>
                <SelectTrigger data-testid="perm-categoria"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="autoveicoli">Autoveicoli</SelectItem>
                  <SelectItem value="motoveicoli">Motoveicoli</SelectItem>
                  <SelectItem value="veicoli_commerciali">Veicoli commerciali</SelectItem>
                  <SelectItem value="auto_storica">Auto storica</SelectItem>
                  <SelectItem value="roulotte_camper">Roulotte / Camper</SelectItem>
                  <SelectItem value="altro">Altro</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2 pt-6">
              <Switch checked={!!form.flag_n1_autocarro}
                onCheckedChange={(b) => set({ flag_n1_autocarro: b })}
                data-testid="perm-flag-n1" />
              <Label className="text-sm">N1 Autocarro</Label>
            </div>
            <div>
              <Label>Targa</Label>
              <Input value={form.targa || ""} placeholder="AB123CD"
                onChange={(e) => set({ targa: (e.target.value || "").toUpperCase() })}
                data-testid="perm-targa" />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label>Marca</Label>
              <Input value={form.marca || ""} placeholder="Fiat"
                onChange={(e) => set({ marca: e.target.value })}
                data-testid="perm-marca" />
            </div>
            <div>
              <Label>Modello</Label>
              <Input value={form.modello || ""} placeholder="Panda"
                onChange={(e) => set({ modello: e.target.value })}
                data-testid="perm-modello" />
            </div>
            <div>
              <Label>Versione</Label>
              <Input value={form.versione || ""}
                onChange={(e) => set({ versione: e.target.value })}
                data-testid="perm-versione" />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label>Prima immatricolazione</Label>
              <Input type="date" value={form.prima_immatricolazione || ""}
                onChange={(e) => set({ prima_immatricolazione: e.target.value })}
                data-testid="perm-prima-imm" />
            </div>
            <div>
              <Label>Km</Label>
              <Input type="number" min="0" value={form.km ?? ""}
                onChange={(e) => set({ km: e.target.value ? parseInt(e.target.value, 10) : null })}
                data-testid="perm-km" />
            </div>
            <div>
              <Label>Data previsto arrivo</Label>
              <Input type="date" value={form.data_previsto_arrivo || ""}
                onChange={(e) => set({ data_previsto_arrivo: e.target.value })}
                data-testid="perm-data-arrivo" />
            </div>
          </div>
        </div>

        {/* Sezione 2: Caratteristiche */}
        <div className="space-y-3 py-2">
          <div className="text-xs font-semibold uppercase text-slate-500 tracking-wide">Caratteristiche</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label>Alimentazione</Label>
              <Select value={form.carburante || ""}
                      onValueChange={(v) => set({ carburante: v || null })}>
                <SelectTrigger data-testid="perm-carburante"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="benzina">Benzina</SelectItem>
                  <SelectItem value="diesel">Diesel</SelectItem>
                  <SelectItem value="gpl">GPL</SelectItem>
                  <SelectItem value="metano">Metano</SelectItem>
                  <SelectItem value="ibrida">Ibrida</SelectItem>
                  <SelectItem value="elettrica">Elettrica</SelectItem>
                  <SelectItem value="ibrida_plug_in">Ibrida plug-in</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Cambio</Label>
              <Select value={form.cambio || ""}
                      onValueChange={(v) => set({ cambio: v || null })}>
                <SelectTrigger data-testid="perm-cambio"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="manuale">Manuale</SelectItem>
                  <SelectItem value="automatico">Automatico</SelectItem>
                  <SelectItem value="semiautomatico">Semiautomatico</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Colore</Label>
              <Select value={form.colore || ""}
                      onValueChange={(v) => set({ colore: v || null })}>
                <SelectTrigger data-testid="perm-colore"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="nero">Nero</SelectItem>
                  <SelectItem value="grigio">Grigio</SelectItem>
                  <SelectItem value="argento">Argento</SelectItem>
                  <SelectItem value="bianco">Bianco</SelectItem>
                  <SelectItem value="rosso">Rosso</SelectItem>
                  <SelectItem value="blu_azzurro">Blu / Azzurro</SelectItem>
                  <SelectItem value="verde">Verde</SelectItem>
                  <SelectItem value="giallo">Giallo</SelectItem>
                  <SelectItem value="arancione">Arancione</SelectItem>
                  <SelectItem value="marrone">Marrone</SelectItem>
                  <SelectItem value="beige">Beige</SelectItem>
                  <SelectItem value="bronzo">Bronzo</SelectItem>
                  <SelectItem value="oro">Oro</SelectItem>
                  <SelectItem value="lilla">Lilla</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Sezione 3: Economici */}
        <div className="space-y-3 py-2">
          <div className="text-xs font-semibold uppercase text-slate-500 tracking-wide">Economici</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label>Tipo di carico</Label>
              <Select value={form.tipo_carico || ""}
                      onValueChange={(v) => set({ tipo_carico: v || null })}>
                <SelectTrigger data-testid="perm-tipo-carico"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="conto_proprio_iva">Conto Proprio IVA</SelectItem>
                  <SelectItem value="conto_proprio_art36">Conto Proprio Art.36</SelectItem>
                  <SelectItem value="conto_terzi">Conto Terzi</SelectItem>
                  <SelectItem value="conto_terzi_iva">Conto Terzi IVA</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Aliquota IVA carico</Label>
              <CodiceIvaPicker value={form.iva_codice_carico}
                onChange={(v) => set({ iva_codice_carico: v })}
                testId="perm-iva-codice" />
            </div>
            <div>
              <Label>Prezzo di ritiro pattuito</Label>
              <div className="border-l-4 border-amber-400 pl-2 -ml-2 rounded">
                <Input type="number" step="0.01" value={form.valore_ritiro ?? ""}
                  onChange={(e) => {
                    // Sprint PERMUTA.2 — Campo UNICO: alimenta sia valore_ritiro
                    // (sconto sulla vendita) sia prezzo_acquisto (futuro carico
                    // veicolo al travaso). Stesso input, due ruoli diversi
                    // (ruoli separati = nessun doppio conteggio).
                    const raw = e.target.value;
                    const num = raw ? parseFloat(raw) : null;
                    set({ valore_ritiro: raw, prezzo_acquisto: num });
                  }}
                  data-testid="perm-prezzo-ritiro"
                  className="font-mono text-right" />
              </div>
              <div className="text-[11px] text-slate-500 mt-1">
                Diventa "Prezzo acquisto" del veicolo permuta quando la
                pratica passa a <span className="font-medium">venduta</span>.
              </div>
            </div>
          </div>
        </div>

        {/* Sezione 4: Note */}
        <div className="py-2">
          <Label>Note</Label>
          <Textarea rows={2} value={form.note || ""}
            onChange={(e) => set({ note: e.target.value })}
            data-testid="perm-note" />
        </div>

        <DialogFooter className="gap-2">
          {/* Bottone aggancia veicolo esistente */}
          {onAgganciaVehicle && (
            <Button type="button" variant="outline"
              onClick={() => onAgganciaVehicle((vid, snap) => {
                set({
                  vehicle_id_agganciato: vid,
                  marca: snap?.marca || form.marca,
                  modello: snap?.modello || form.modello,
                  versione: snap?.versione || form.versione,
                  targa: snap?.targa || form.targa,
                });
              })}
              data-testid="perm-aggancia-existing">
              <Car className="h-4 w-4 mr-1" /> Aggancia veicolo esistente
            </Button>
          )}
          <div className="flex-1" />
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button onClick={() => {
            onSave({
              veicolo_descrizione: form.veicolo_descrizione,
              valore_ritiro: parseFloat(form.valore_ritiro || 0) || 0,
              // iva_su_permuta deprecato (Sprint PERMUTA.2): l'aliquota è
              // già nel CodiceIvaPicker `iva_codice_carico`. Inviato false
              // per backward compat backend (campo ancora nel modello).
              iva_su_permuta: false,
              note: form.note || null,
              // Sprint PERMUTA.1 — campi anagrafici
              categoria_veicolo: form.categoria_veicolo || null,
              flag_n1_autocarro: !!form.flag_n1_autocarro,
              targa: form.targa || null,
              km: form.km || null,
              marca: form.marca || null,
              modello: form.modello || null,
              versione: form.versione || null,
              prima_immatricolazione: form.prima_immatricolazione || null,
              carburante: form.carburante || null,
              cambio: form.cambio || null,
              colore: form.colore || null,
              tipo_carico: form.tipo_carico || null,
              iva_codice_carico: form.iva_codice_carico || null,
              prezzo_acquisto: form.prezzo_acquisto != null ? parseFloat(form.prezzo_acquisto) : null,
              data_previsto_arrivo: form.data_previsto_arrivo || null,
              vehicle_id_agganciato: form.vehicle_id_agganciato || null,
            });
          }} data-testid="perm-save">Salva</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// -------- Popup caparra --------
function CaparraPopup({ open, onOpenChange, initial, onSave }) {
  const [form, setForm] = useState(initial || { modalita: "bonifico" });
  useEffect(() => { setForm(initial || { modalita: "bonifico" }); }, [initial, open]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]" data-testid="cap-popup">
        <DialogHeader><DialogTitle>Pagamento caparra</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div><Label>Importo (€) *</Label>
            <Input type="number" step="0.01" value={form.importo ?? ""}
              onChange={(e) => setForm({ ...form, importo: e.target.value })} data-testid="cap-importo" /></div>
          <div><Label>Data</Label>
            <Input type="date" value={form.data || ""} onChange={(e) => setForm({ ...form, data: e.target.value })} /></div>
          <div><Label>Modalità *</Label>
            <Select value={form.modalita || "bonifico"} onValueChange={(v) => setForm({ ...form, modalita: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {MODALITA_CAPARRA.map((m) => <SelectItem key={m.v} value={m.v}>{m.l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button onClick={() => onSave({
            importo: parseFloat(form.importo || 0) || 0,
            data: form.data || null,
            modalita: form.modalita || "bonifico",
          })} data-testid="cap-save">Salva</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// -------- Sezione righe --------
function SezioneRighe({ titolo, icon: Icon, categoria, righe, onAdd, onEdit, onDelete, color }) {
  const totAcq = righe.reduce((s, r) => s + (r.importo_acquisto || 0), 0);
  const totVen = righe.reduce((s, r) => s + (r.importo_vendita || 0), 0);
  const anyHiddenAcq = righe.some((r) => r.importo_acquisto === null);
  // Sprint 4B.2 (B8): default collapsed se vuota
  const [open, setOpen] = useState(righe.length > 0);
  useEffect(() => { setOpen(righe.length > 0); }, [righe.length]);
  return (
    <Card className="p-3" data-testid={`card-${categoria}`}>
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="flex items-center gap-2 hover:text-blue-600 transition-colors"
          data-testid={`toggle-${categoria}`}
          data-open={open ? "true" : "false"}
        >
          <span className={`text-slate-400 text-xs transition-transform ${open ? "rotate-90" : ""}`}>▶</span>
          <Icon className={`h-4 w-4 ${color}`} />
          <h3 className="font-heading text-base font-semibold text-slate-900">{titolo}</h3>
          <span className="text-xs text-slate-400">({righe.length})</span>
          {!open && righe.length > 0 && (
            <span className="text-[11px] text-slate-500 font-mono ml-1">{formatEUR(totVen)}</span>
          )}
        </button>
        <Button size="sm" variant="outline" onClick={onAdd} data-testid={`add-${categoria}`}>
          <Plus className="h-4 w-4 mr-1" /> Aggiungi
        </Button>
      </div>
      {open && (
        righe.length === 0 ? (
          <div className="text-sm text-slate-400 italic py-2 mt-2">Nessuna riga</div>
        ) : (
          <table className="w-full text-sm mt-2 table-fixed">
            <thead className="text-[11px] uppercase text-slate-500 border-b">
              <tr>
                <th className="text-left py-1">Descrizione</th>
                {categoria !== "sconti" && <th className="text-right py-1 text-amber-700 w-[120px]">Acquisto</th>}
                <th className="text-right py-1 text-blue-700 w-[120px]">{categoria === "sconti" ? "Sconto" : "Vendita"}</th>
                <th className="w-[70px]"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {righe.map((r) => (
                <tr key={r.id} className="hover:bg-slate-50" data-testid={`riga-${categoria}-${r.id}`}>
                  <td className="py-1.5">{r.descrizione}</td>
                  {categoria !== "sconti" && (
                    <td className="py-1.5 text-right font-mono text-amber-700 border-l-2 border-amber-200">
                      {formatEURMasked(r.importo_acquisto)}
                    </td>
                  )}
                  <td className={`py-1.5 text-right font-mono border-l-2 ${categoria === "sconti" ? "border-rose-200 text-rose-700 bg-rose-50/40" : "border-blue-200 text-blue-700"}`}>
                    {formatEUR(r.importo_vendita)}
                  </td>
                  <td className="py-1.5 text-right">
                    <button onClick={() => onEdit(r)} className="text-slate-400 hover:text-blue-600 p-0.5" title="Modifica">
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button onClick={() => onDelete(r)} className="text-slate-400 hover:text-rose-600 p-0.5" title="Elimina">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
              {/* Sprint 4F2.3 E1: riga totali visibile solo se ≥2 voci (pattern Garanzie) */}
              {righe.length >= 2 && (
              <tr className="font-semibold text-[12px]">
                {categoria !== "sconti" ? (
                  <>
                    <td className="py-1.5 text-right" colSpan={1}>Totali:</td>
                    <td className="py-1.5 text-right font-mono text-amber-800">
                      {anyHiddenAcq ? "" : formatEUR(totAcq)}
                    </td>
                    <td className="py-1.5 text-right font-mono text-blue-800">{formatEUR(totVen)}</td>
                    <td></td>
                  </>
                ) : (
                  <>
                    <td className="py-1.5 text-right">Totale sconti:</td>
                    <td className="py-1.5 text-right font-mono text-rose-700">{formatEUR(totVen)}</td>
                    <td></td>
                  </>
                )}
              </tr>
              )}
            </tbody>
          </table>
        )
      )}
    </Card>
  );
}

// -------- Indicatore margine inline (no sfondo) --------
function MargineInline({ margine, nascosto, canSeeNumbers, onGoTo }) {
  if (margine?.semaforo === "non_determinabile") {
    return (
      <div className="flex items-center gap-2.5 mt-1.5 text-sm" data-testid="margine-inline">
        <span className="inline-block w-3 h-3 rounded-full bg-slate-300" data-testid="margine-semaforo" />
        <span className="font-bold text-slate-600" data-testid="margine-non-determinabile">Margine non determinabile — tipo di carico mancante</span>
        {onGoTo && (
          <button type="button" className="text-blue-700 underline underline-offset-2 text-xs" data-testid="margine-carico-link"
            onClick={() => onGoTo("importi", "vehicle-tipo-carico-select")}>imposta il carico</button>
        )}
      </div>
    );
  }
  // Sprint FIX-MONITOR2: il semaforo + label "Margine raggiunto/non raggiunto"
  // restano sempre visibili (in Monitor Cliente serve al venditore vedere il SE,
  // senza vedere il QUANTO). Si nascondono solo i NUMERI (importo + percentuale).
  const verde = margine?.semaforo === "verde";
  const colorText = verde ? "text-emerald-700" : "text-rose-700";
  const colorDot = verde ? "bg-emerald-500" : "bg-rose-500";
  const showNumbers = !nascosto
    && canSeeNumbers
    && margine?.margine_netto !== null
    && margine?.margine_netto !== undefined;
  return (
    <div className="flex items-center gap-2.5 mt-1.5" data-testid="margine-inline">
      <span className={`inline-block w-3 h-3 rounded-full ${colorDot}`} data-testid="margine-semaforo" />
      <span className={`font-bold text-sm ${colorText}`}>
        {verde ? "Margine raggiunto" : "Margine non raggiunto"}
      </span>
      {showNumbers && (
        <span className={`font-bold text-sm font-mono ${colorText}`} data-testid="margine-numbers">
          · {formatEUR(margine?.margine_netto)} ({margine?.margine_percentuale}%)
        </span>
      )}
    </div>
  );
}

// -------- Box residuo colorato --------
function ResiduoBox({ margine }) {
  // Sprint 2 — residuo e componenti letti dal server: nessun calcolo qui.
  const diff = margine?.residuo_da_contabilizzare ?? 0;
  const compFin = margine?.componente_finanziamento ?? 0;
  const compCli = margine?.componente_cliente ?? 0;
  let bg, label, valore, dataTestState;
  if (diff > 0) {
    bg = "bg-rose-600"; label = "Residuo da incassare";
    valore = formatEUR(diff); dataTestState = "rosso";
  } else if (diff === 0) {
    bg = "bg-emerald-600"; label = "Saldo";
    valore = "Pagamento completo"; dataTestState = "verde";
  } else {
    bg = "bg-blue-600"; label = "Pagamenti in eccesso";
    valore = formatEUR(Math.abs(diff)); dataTestState = "blu";
  }
  return (
    <div className={`rounded-lg p-4 ${bg} text-white shadow-sm`} data-testid="residuo-box" data-state={dataTestState}>
      <div className="text-[11px] uppercase tracking-wider opacity-90">{label}</div>
      <div className="font-heading text-2xl mt-1 font-mono">{valore}</div>
      {compFin !== 0 && (
        <div className="flex justify-between text-xs mt-2 opacity-95" data-testid="residuo-di-cui-finanziamento">
          <span className="pl-3">di cui Finanziamento</span><span className="font-mono">{formatEUR(compFin)}</span>
        </div>
      )}
      {compFin !== 0 && (
        <div className="flex justify-between text-xs mt-1 opacity-95" data-testid="residuo-di-cui-cliente">
          <span className="pl-3">di cui Cliente</span><span className="font-mono">{formatEUR(compCli)}</span>
        </div>
      )}
    </div>
  );
}

// Sprint 2 — regime IVA risolto dal carico del veicolo, in sola lettura.
// Sprint 9 — se il regime della vendita diverge dal carico mostra i due valori distinti e «Allinea al carico».
function RegimeIvaRow({ v, onGoTo, canUnlock, isTempUnlocked, onAllineato }) {
  const r = v?.regime_documento;
  if (!r) return null;
  if (!r.conto) {
    return (
      <div className="px-3 py-2 bg-amber-50 border border-amber-300 rounded-md text-xs text-amber-900" data-testid="regime-iva-row">
        <span className="font-semibold">Regime IVA:</span> <span data-testid="regime-iva-non-determinabile">non determinabile — tipo di carico mancante</span>
        {onGoTo && (
          <> · <button type="button" className="text-blue-700 underline underline-offset-2" data-testid="regime-iva-link"
            onClick={() => onGoTo("importi", "vehicle-tipo-carico-select")}>imposta il carico</button></>
        )}
      </div>
    );
  }
  const al = r.allineamento || {};
  const aliq = v?.iva_aliquota != null ? `${Number(v.iva_aliquota).toLocaleString("it-IT")}%` : "—";
  return (
    <div className={`px-3 py-2 border rounded-md text-xs text-slate-700 ${al.disallineato ? "bg-amber-50 border-amber-300" : "bg-white"}`} data-testid="regime-iva-row">
      {al.disallineato ? (
        <div data-testid="regime-iva-disallineato">
          <span className="font-semibold">Regime IVA:</span> vendita <b data-testid="regime-iva-vendita">{al.vendita}</b>
          <span className="text-slate-500"> · </span>carico del veicolo <b data-testid="regime-iva-carico">{al.carico}</b>
        </div>
      ) : (
        <>
          <span className="font-semibold">Regime IVA:</span> {r.etichetta || "non risolto"} — {aliq}
          <span className="text-slate-500"> · da carico veicolo: {VEHICLE_TIPO_CARICO_LABELS[r.tipo_carico] || r.tipo_carico || "non impostato"}</span>
        </>
      )}
      {onGoTo && (
        <> · <button type="button" className="text-blue-700 underline underline-offset-2" data-testid="regime-iva-link"
          onClick={() => onGoTo("importi", "vehicle-tipo-carico-select")}>modifica il carico</button></>
      )}
      {r.coerente === false && (r.avvisi || []).length > 0 && (
        <div className="mt-1 text-amber-700" data-testid="regime-iva-avvisi">{r.avvisi.join(" · ")}</div>
      )}
      {al.disallineato && <AllineaCarico v={v} canUnlock={canUnlock} isTempUnlocked={isTempUnlocked} onDone={onAllineato} />}
    </div>
  );
}

function AllineaCarico({ v, canUnlock, isTempUnlocked, onDone }) {
  const [anteprima, setAnteprima] = useState(null);
  const [busy, setBusy] = useState(false);
  const annullata = v.tipo === "pratica" && v.status === "annullata";
  const consegnata = v.tipo === "pratica" && v.status === "consegnata";
  let blocco = null;
  if (annullata) blocco = "Pratica annullata: è in sola lettura. Riaprila per allineare il regime al carico.";
  else if (consegnata && !canUnlock) blocco = "Pratica consegnata: per correggere il regime serve il permesso \"Agisci su pratica consegnata\".";
  else if (consegnata && !isTempUnlocked) blocco = "Pratica consegnata: attiva lo sblocco temporaneo per correggere il regime.";
  const apri = async () => {
    setBusy(true);
    try { setAnteprima((await api.get(`/vendite/${v.id}/allinea-carico`)).data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };
  const conferma = async () => {
    setBusy(true);
    try {
      await api.post(`/vendite/${v.id}/allinea-carico`, { sblocco: !!isTempUnlocked });
      toast.success("Regime allineato al carico del veicolo");
      setAnteprima(null);
      onDone && onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-2 flex items-center gap-2 flex-wrap">
      <Button size="sm" variant="outline" className="h-7 text-xs" disabled={!!blocco || busy} onClick={apri} data-testid="allinea-carico-btn">
        Allinea al carico
      </Button>
      {blocco && <span className="text-[11px] text-slate-600" data-testid="allinea-carico-motivo">{blocco}</span>}
      {anteprima && (
        <Dialog open onOpenChange={(o) => !o && setAnteprima(null)}>
          <DialogContent className="max-w-lg" data-testid="allinea-carico-dialog">
            <DialogHeader><DialogTitle>Allinea il regime IVA al carico</DialogTitle></DialogHeader>
            {anteprima.da_allineare ? (
              <>
                <p className="text-xs text-slate-700">Cambiando il regime cambiano lo scorporo IVA (imponibile e imposta). Il margine è riportato per confronto: oggi è calcolato sugli importi lordi e non varia con il regime.</p>
                {consegnata && (
                  <p className="text-xs text-rose-800" data-testid="allinea-carico-consegnata">
                    La pratica è consegnata: la correzione riguarda un documento fiscale già emesso. La fattura va sistemata di conseguenza.
                  </p>
                )}
                <ConfrontoRegime a={anteprima} testid="allinea-carico-confronto" />
              </>
            ) : <p className="text-sm">{anteprima.motivo || "Il regime è già allineato al carico."}</p>}
            <DialogFooter>
              <Button variant="outline" onClick={() => setAnteprima(null)} data-testid="allinea-carico-annulla">Annulla</Button>
              {anteprima.da_allineare && <Button onClick={conferma} disabled={busy} data-testid="allinea-carico-conferma">Allinea</Button>}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function CvtCoerenzaAlert({ margine }) {
  const c = margine?.cvt_coerenza;
  if (!c || c.coerente) return null;
  // Sprint UI-GLOBALE — nessun comando in questo punto: centro avvisi
  return <AvvisoPagina id="cvt-coerenza" livello="segnalazione"
    testo={`Polizza CVT incoerente: margine polizza ${formatEUR(c.margine_cvt)} ≠ flussi registrati ${formatEUR(c.flussi)}. Verifica i dati della polizza.`} />;
}

// -------- Empty state (no vendita) --------
function EmptyVendita({ vehicleStatus, onCreatePratica, canCreate = true, onSwitchToPreventivi }) {
  // Sprint 4B.1 (B6): rimosso pulsante "CREA PRATICA". Sostituito da messaggio
  // informativo + link al tab Preventivi.
  return (
    <Card className="p-10 text-center" data-testid="vendita-empty">
      <CreditCard className="h-12 w-12 text-slate-300 mx-auto mb-3" />
      <div className="text-slate-800 font-medium mb-2">Nessuna pratica di vendita per questo veicolo</div>
      <div className="text-slate-600 text-sm max-w-xl mx-auto leading-relaxed">
        Per iniziare una trattativa:
        <ol className="text-left mt-2 list-decimal pl-6 space-y-0.5">
          <li>Vai al tab <span className="font-semibold">Preventivi</span> del veicolo</li>
          <li>Crea un nuovo preventivo</li>
          <li>Conferma il preventivo per impegnare il veicolo</li>
        </ol>
      </div>
      {typeof onSwitchToPreventivi === "function" && (
        <div className="mt-5 flex justify-center">
          <Button
            variant="outline"
            onClick={onSwitchToPreventivi}
            data-testid="btn-vai-preventivi"
          >
            <FileText className="h-4 w-4 mr-1.5" /> Vai a Preventivi
          </Button>
        </div>
      )}
      <div className="mt-4 text-[11px] uppercase text-slate-400 tracking-wider">
        Stato attuale veicolo: {vehicleStatus || "—"}
      </div>
    </Card>
  );
}

// -------- Lock banner per contratto chiuso (Sprint VEND-UNLOCK) --------
// Comportamento:
//  - venduta:    se canUnlock → banner blu informativo; altrimenti banner ambra.
//  - consegnata: il permesso NON sblocca da solo; serve clic esplicito su
//    "Sblocca temporaneamente". Tre stati visivi:
//      • Nessun permesso  → ambra "Per modificare serve il permesso ...".
//      • Permesso, locked → ambra + bottone "Sblocca temporaneamente".
//      • Permesso, unlock → blu  "Modifica temporanea attiva" + bottone Blocca.
function LockBanner({ vendita, canUnlock, isTempUnlocked, onTempUnlock, onTempLock }) {
  if (!vendita || !["venduta", "consegnata"].includes(vendita.status)) return null;
  const isConsegnata = vendita.status === "consegnata";
  const permLabel = isConsegnata ? "Agisci su pratica consegnata" : "Agisci su pratica venduta";
  const incassi = ` I pagamenti restano modificabili da chi ha il permesso "Gestione incassi".`;
  // Sprint UI-GLOBALE — in pagina solo con un comando (Sblocca / Blocca di nuovo); altrimenti centro avvisi
  const conAzione = isConsegnata && canUnlock;
  const testo = !isConsegnata && canUnlock
    ? `Pratica venduta — modificabile grazie al permesso "${permLabel}".`
    : `Pratica ${isConsegnata ? "consegnata" : "venduta"}. Per modificarla serve il permesso "${permLabel}".${incassi}`;
  return (
    <AvvisoPagina id="vendita-blocco" conAzione={conAzione} livello={!isConsegnata && canUnlock ? "info" : "segnalazione"} testo={testo}>
      <LockBannerAzione permLabel={permLabel} isTempUnlocked={isTempUnlocked} onTempUnlock={onTempUnlock} onTempLock={onTempLock} />
    </AvvisoPagina>
  );
}

function LockBannerAzione({ permLabel, isTempUnlocked, onTempUnlock, onTempLock }) {
  if (!isTempUnlocked) {
    // Permesso ma non ancora sbloccato → CTA esplicita
    return (
      <div className="rounded-lg border border-amber-300 bg-amber-50 text-amber-900 text-sm px-4 py-2.5 flex items-start gap-3 justify-between mb-3" data-testid="lock-banner-locked-unlockable">
        <div className="flex items-start gap-2">
          <LockIcon className="h-4 w-4 mt-0.5 shrink-0" />
          <span>
            <b>Pratica consegnata — protetta.</b>{" "}
            Hai il permesso <b>"{permLabel}"</b>. Clicca su <b>Sblocca temporaneamente</b>{" "}
            per abilitare le modifiche; il blocco verrà ripristinato automaticamente al salvataggio.
          </span>
        </div>
        <Button
          size="sm"
          variant="outline"
          className="border-amber-500 text-amber-800 hover:bg-amber-100 shrink-0"
          onClick={onTempUnlock}
          data-testid="vendita-sblocca-temp-btn"
        >
          <Unlock className="h-4 w-4 mr-1.5" /> Sblocca temporaneamente
        </Button>
      </div>
    );
  }

  // Permesso + sbloccato
  return (
    <div className="rounded-lg border border-blue-300 bg-blue-50 text-blue-900 text-sm px-4 py-2.5 flex items-start gap-3 justify-between mb-3" data-testid="lock-banner-temp-unlocked">
      <div className="flex items-start gap-2">
        <Unlock className="h-4 w-4 mt-0.5 shrink-0" />
        <span>
          <b>Modifica temporanea attiva.</b>{" "}
          Pratica consegnata sbloccata grazie al permesso <b>"{permLabel}"</b>.
          Il blocco si ripristina automaticamente al primo salvataggio.
        </span>
      </div>
      <Button
        size="sm"
        variant="outline"
        className="border-blue-400 text-blue-800 hover:bg-blue-100 shrink-0"
        onClick={onTempLock}
        data-testid="vendita-blocca-di-nuovo-btn"
      >
        <LockIcon className="h-4 w-4 mr-1.5" /> Blocca di nuovo
      </Button>
    </div>
  );
}


// Sprint 4Z.1 F3 — Banner pratica annullata + Riapri
function AnnullataBanner({ vendita, canReopen, onReopened }) {
  // Sprint 3: la riapertura chiede il cliente (azzerato dall'annullamento) e lo invia.
  const [openRiapri, setOpenRiapri] = useState(false);
  // Sprint UI-GLOBALE — senza il permesso di riaprire non c'è comando: l'avviso va nel centro avvisi
  if (!canReopen) {
    return <AvvisoPagina id="vendita-annullata" livello="critico"
      testo={`Pratica annullata — archivio read-only.${vendita.annullata_motivo ? ` Motivo: ${vendita.annullata_motivo}.` : ""} Per modificare, riaprire la pratica.`} />;
  }
  return (
    <div className="rounded-lg border border-rose-300 bg-rose-50 text-rose-900 text-sm px-4 py-2.5 flex items-start justify-between gap-3 mb-3"
         data-testid="annullata-banner">
      <div className="flex items-start gap-2">
        <Trash2 className="h-4 w-4 mt-0.5 shrink-0" />
        <span>
          <b>Pratica annullata — archivio read-only.</b>
          {vendita.annullata_motivo && <> Motivo: <i>{vendita.annullata_motivo}</i>.</>}
          {" "}Per modificare, riaprire la pratica.
        </span>
      </div>
      {canReopen && (
        <Button
          size="sm"
          variant="outline"
          className="border-rose-400 text-rose-700 hover:bg-rose-100"
          onClick={() => setOpenRiapri(true)}
          data-testid="btn-riapri-pratica"
        >
          Riapri pratica
        </Button>
      )}
      <RiapriPraticaDialog open={openRiapri} onOpenChange={setOpenRiapri} vendita={vendita}
        motivo="Riapertura da archivio annullate" onDone={onReopened} />
    </div>
  );
}

// -------- Header info badges (4 nomi) --------
// Sprint 10 — qualità dell'acquirente: derivata dall'anagrafica sul server, sempre correggibile
const QUALITA_LABELS = { consumatore: "Consumatore", impresa: "Impresa", commerciante: "Commerciante" };
// Sprint 12 — da venduta/consegnata segue il blocco della pratica (permesso + sblocco temporaneo)
function QualitaAcquirente({ vendita, onDone, locked, sblocco }) {
  const info = vendita.acquirente_qualita_info;
  if (!info) return null;
  const salva = async (val) => {
    try {
      await api.patch(`/vendite/${vendita.id}`, { acquirente_qualita: val === "__deriva" ? null : val, ...(sblocco ? { sblocco: true } : {}) });
      toast.success("Qualità dell'acquirente aggiornata");
      onDone && onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  const evid = info.da_scegliere || info.avviso;
  return (
    <div className={`flex items-center gap-1.5 px-2 py-1 border rounded-md text-xs ${evid ? "bg-amber-50 border-amber-300" : "bg-white"}`} data-testid="qualita-acquirente">
      <span className="text-slate-500">Qualità acquirente</span>
      <Select value={info.valore} onValueChange={salva} disabled={!!locked}>
        <SelectTrigger className="h-6 w-[130px] text-xs" data-testid="qualita-acquirente-select"
          title={locked ? "Pratica chiusa: serve il permesso e lo sblocco temporaneo per modificarla" : undefined}><SelectValue /></SelectTrigger>
        <SelectContent>
          {Object.entries(QUALITA_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
          {info.origine === "manuale" && <SelectItem value="__deriva">Ripristina dall'anagrafica</SelectItem>}
        </SelectContent>
      </Select>
      <span className="text-slate-500" data-testid="qualita-acquirente-origine">
        {info.origine === "manuale" ? "scelta dall'operatore" : "derivata dall'anagrafica"}
      </span>
      {info.da_scegliere && <span className="text-amber-800 font-medium" data-testid="qualita-acquirente-da-scegliere">· da scegliere</span>}
      {info.avviso && <span className="text-amber-800" data-testid="qualita-acquirente-avviso">· {info.avviso}</span>}
    </div>
  );
}

function HeaderBadges({ vendita, vehicle, anagraficheCache, usersCache, canEditVenditore,
                        onEditVenditore, canEditAnagrafica, anagraficaPermReason,
                        onEditAnagrafica, onQualitaChanged, qualitaLocked, qualitaSblocco }) {
  const cli = anagraficheCache[vendita.cliente_id];
  const co = anagraficheCache[vendita.cointestatario_vendita_id];
  const fornitore = vehicle?.supplier_id ? anagraficheCache[vehicle.supplier_id] : null;
  const v = usersCache[vendita.venditore_id];
  const [viewAnagId, setViewAnagId] = useState(null); // Sprint 4X.2

  const nameAnag = (a) => {
    if (!a) return null;
    if (a.tipo_persona === "azienda") return a.ragione_sociale;
    return [a.cognome, a.nome].filter(Boolean).join(" ");
  };
  const nameUser = (u) => u ? ([u.first_name, u.last_name].filter(Boolean).join(" ") || u.email) : null;

  const Pill = ({ label, name, viewId, dataTestid, onClick, onAdd, addLabel,
                  editTitle, editDisabled, editTooltip, editTestid }) => (
    <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-slate-200 bg-white text-[12px]" data-testid={dataTestid}>
      <span className="text-slate-500 font-medium uppercase tracking-wider text-[10px]">{label}:</span>
      {name ? (
        <span className="text-slate-800">{name}</span>
      ) : onAdd ? (
        <button type="button"
                onClick={onAdd}
                className="text-blue-600 hover:underline font-medium flex items-center gap-1"
                data-testid={`${dataTestid}-add`}>
          <Plus className="h-3 w-3" /> {addLabel || "Aggiungi"}
        </button>
      ) : (
        <span className="text-slate-400">—</span>
      )}
      {viewId && (
        <button type="button"
                onClick={() => setViewAnagId(viewId)}
                className="text-slate-400 hover:text-blue-600 ml-0.5" title={`Visualizza ${label}`}
                data-testid={`${dataTestid}-eye`}>
          <Eye className="h-3 w-3" />
        </button>
      )}
      {onClick && (
        <button onClick={onClick} className="text-slate-400 hover:text-blue-600 ml-0.5" title="Modifica">
          <Pencil className="h-3 w-3" />
        </button>
      )}
      {editTitle && (
        <button
          type="button"
          onClick={editDisabled ? undefined : editTitle}
          className={`ml-0.5 ${editDisabled
            ? "text-slate-300 cursor-not-allowed"
            : "text-slate-400 hover:text-blue-600"}`}
          title={editDisabled ? (editTooltip || "Permesso non disponibile") : "Modifica"}
          data-testid={editTestid}
          disabled={!!editDisabled}
        >
          <Pencil className="h-3 w-3" />
        </button>
      )}
    </div>
  );

  return (
    <div className="flex flex-wrap gap-2 mt-2" data-testid="header-badges">
      <Pill label="Venditore" name={vendita.venditore_id ? <NomeVenditore vendita={vendita} testid="badge-venditore-nome" /> : null}
            onClick={canEditVenditore ? onEditVenditore : null}
            dataTestid="badge-venditore" />
      <div className="self-center"><RigaOpportunita venditaId={vendita.id} /></div>
      <Pill label="Cliente"
            name={nameAnag(cli) || (vendita.cliente_id ? vendita.cliente_id?.slice(0, 8) : null)}
            viewId={vendita.cliente_id || null}
            onAdd={!vendita.cliente_id && onEditAnagrafica
              ? () => onEditAnagrafica("cliente_id", null) : null}
            addLabel="Aggiungi cliente"
            editTitle={onEditAnagrafica && vendita.cliente_id
              ? () => onEditAnagrafica("cliente_id", vendita.cliente_id) : null}
            editDisabled={!canEditAnagrafica}
            editTooltip={anagraficaPermReason}
            editTestid="badge-cliente-edit"
            dataTestid="badge-cliente" />
      <QualitaAcquirente vendita={vendita} onDone={onQualitaChanged} locked={qualitaLocked} sblocco={qualitaSblocco} />
      <Pill label="Cointestatario" name={nameAnag(co)}
            viewId={vendita.cointestatario_vendita_id || null}
            onAdd={!vendita.cointestatario_vendita_id && onEditAnagrafica && canEditAnagrafica
              ? () => onEditAnagrafica("cointestatario_vendita_id", null) : null}
            addLabel="Aggiungi cointestatario"
            editTitle={onEditAnagrafica && vendita.cointestatario_vendita_id
              ? () => onEditAnagrafica("cointestatario_vendita_id", vendita.cointestatario_vendita_id)
              : null}
            editDisabled={!canEditAnagrafica}
            editTooltip={anagraficaPermReason}
            editTestid="badge-cointestatario-edit"
            dataTestid="badge-cointestatario" />
      <Pill label="Fornitore veicolo" name={nameAnag(fornitore)}
            viewId={vehicle?.supplier_id || null}
            dataTestid="badge-fornitore" />
      <AnagraficaViewModal
        open={!!viewAnagId}
        onOpenChange={(o) => !o && setViewAnagId(null)}
        anagraficaId={viewAnagId}
      />
    </div>
  );
}


// Sprint 4Z A1 — Modale per cambiare/aggiungere cliente o cointestatario
function EditAnagraficaModal({ open, onOpenChange, vendita, field, currentId, onSaved }) {
  const [picked, setPicked] = useState(currentId || null);
  const [submitting, setSubmitting] = useState(false);
  useEffect(() => { setPicked(currentId || null); }, [currentId, open]);
  // Sprint CRM-3 — vendita collegata a un'opportunità: voci dal contatto CRM (solo nella finestra «Cliente»)
  const crm = useOpportunitaVendita(open && field === "cliente_id" ? vendita?.id : null);
  const [esitoCrm, setEsitoCrm] = useState(null);
  useEffect(() => {
    if (open && !currentId && crm?.contatto?.anagrafica_id) setPicked(crm.contatto.anagrafica_id);
  }, [open, currentId, crm]);

  const isCointestatario = field === "cointestatario_vendita_id";
  const titleLabel = isCointestatario ? "Cointestatario" : "Cliente";

  const submit = async () => {
    if (field === "cliente_id" && vendita?.tipo === "pratica" && !picked) {
      toast.error("Le pratiche richiedono sempre un cliente");
      return;
    }
    setSubmitting(true);
    try {
      await api.patch(`/vendite/${vendita.id}`, { [field]: picked || null });
      toast.success(`${titleLabel} ${currentId ? "aggiornato" : "aggiunto"}`);
      if (field === "cliente_id" && picked && crm?.contatto && !crm.contatto.anagrafica_id) {
        const esito = await collegaContatto(crm, picked);  // Sprint CRM-3 — «Collega un'anagrafica esistente»
        if (esito?.differenze?.length) { setEsitoCrm(esito); return; }
      }
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      const msg = e?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : `Errore ${titleLabel.toLowerCase()}`);
    } finally {
      setSubmitting(false);
    }
  };

  const removeAnag = async () => {
    if (field === "cliente_id" && vendita?.tipo === "pratica") {
      toast.error("Le pratiche richiedono sempre un cliente");
      return;
    }
    setSubmitting(true);
    try {
      await api.patch(`/vendite/${vendita.id}`, { [field]: null });
      toast.success(`${titleLabel} rimosso`);
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      const msg = e?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Errore rimozione");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="edit-anagrafica-modal">
        <DialogHeader>
          <DialogTitle>
            {currentId ? `Modifica ${titleLabel.toLowerCase()}` : `Aggiungi ${titleLabel.toLowerCase()}`}
          </DialogTitle>
          <DialogDescription>
            Seleziona un'anagrafica esistente. La modifica genera una entry in audit log.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {field === "cliente_id" && !esitoCrm && <ClienteDaCrm opp={crm} onCreata={(aid, esito) => {
            setPicked(aid);
            if (esito?.differenze?.length) setEsitoCrm(esito);
          }} />}
          {esitoCrm && <DifferenzeAnagrafica esito={esitoCrm} onFine={() => { setEsitoCrm(null); onSaved?.(); onOpenChange(false); }} />}
          {field === "cliente_id" && crm && <div className="text-xs text-slate-600" data-testid="crm-seguita-da">Questa opportunità è seguita da {crm.titolare_nome || "—"}</div>}
          <div>
            <Label>{titleLabel}</Label>
            <AnagraficaPicker value={picked} onChange={setPicked} role="cliente"
                              contextCompanyId={vendita?.company_id || null} />
          </div>
        </div>
        <DialogFooter className="flex justify-between flex-row-reverse">
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
              Annulla
            </Button>
            <Button onClick={submit} disabled={submitting || picked === currentId}
                    data-testid="edit-anagrafica-save">
              {submitting ? "Salvataggio..." : "Salva"}
            </Button>
          </div>
          {currentId && !(field === "cliente_id" && vendita?.tipo === "pratica") && (
            <Button
              type="button"
              variant="ghost"
              className="text-rose-600 hover:bg-rose-50"
              onClick={removeAnag}
              disabled={submitting}
              data-testid="edit-anagrafica-remove"
            >
              <Trash2 className="h-3.5 w-3.5 mr-1" /> Rimuovi
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// -------- Popup pagamento --------
function PagamentoPopup({ open, onOpenChange, initial, onSave }) {
  const [form, setForm] = useState(initial || { tipo: "acconto", modalita: "bonifico" });
  useEffect(() => { setForm(initial || { tipo: "acconto", modalita: "bonifico" }); }, [initial, open]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]" data-testid="pag-popup">
        <DialogHeader>
          <DialogTitle>{initial?.id ? "Modifica" : "Aggiungi"} pagamento</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label>Tipo *</Label>
            <Select value={form.tipo || "acconto"} onValueChange={(v) => setForm({ ...form, tipo: v })}>
              <SelectTrigger data-testid="pag-tipo"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="caparra">Caparra</SelectItem>
                <SelectItem value="acconto">Acconto</SelectItem>
                <SelectItem value="saldo">Saldo</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Importo (€) *</Label>
            <Input type="number" step="0.01" value={form.importo ?? ""}
              onChange={(e) => setForm({ ...form, importo: e.target.value })} data-testid="pag-importo" />
          </div>
          <div>
            <Label>Data</Label>
            <Input type="date" value={form.data || ""} onChange={(e) => setForm({ ...form, data: e.target.value })} />
          </div>
          <div>
            <Label>Modalità *</Label>
            <Select value={form.modalita || "bonifico"} onValueChange={(v) => setForm({ ...form, modalita: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {MODALITA_CAPARRA.map((m) => <SelectItem key={m.v} value={m.v}>{m.l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Note</Label>
            <Textarea rows={2} value={form.note || ""} onChange={(e) => setForm({ ...form, note: e.target.value })} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button onClick={() => {
            const payload = {
              tipo: form.tipo || "acconto",
              importo: parseFloat(form.importo || 0) || 0,
              data: form.data || null,
              modalita: form.modalita || "bonifico",
              note: form.note || null,
            };
            if (form.id) payload.id = form.id;
            onSave(payload);
          }} data-testid="pag-save">Salva</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// -------- Sezione pagamenti multipli --------
function SezionePagamenti({ pagamenti, margine, onAdd, onEdit, onDelete, readOnly }) {
  const tipoLabel = { caparra: "Caparra", acconto: "Acconto", saldo: "Saldo" };
  const sub = (t) => ({
    caparra: margine?.totale_pagamenti_caparra,
    acconto: margine?.totale_pagamenti_acconto,
    saldo: margine?.totale_pagamenti_saldo,
  }[t]);
  return (
    <Card className="p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Banknote className="h-4 w-4 text-emerald-700" />
          <h3 className="font-heading text-lg text-slate-900">Pagamenti</h3>
          <span className="text-xs text-slate-400">({(pagamenti || []).length})</span>
        </div>
        {!readOnly && (
          <Button size="sm" variant="outline" onClick={onAdd} data-testid="add-pagamento">
            <Plus className="h-4 w-4 mr-1" /> Aggiungi pagamento
          </Button>
        )}
      </div>
      {(pagamenti || []).length === 0 ? (
        <div className="text-sm text-slate-400 italic py-2">Nessun pagamento registrato</div>
      ) : (
        <table className="w-full text-sm">
          <thead className="text-[11px] uppercase text-slate-500 border-b">
            <tr>
              <th className="text-left py-1.5">Tipo</th>
              <th className="text-right py-1.5">Importo</th>
              <th className="text-left py-1.5">Data</th>
              <th className="text-left py-1.5">Modalità</th>
              <th className="text-left py-1.5">Note</th>
              {!readOnly && <th className="w-[80px]"></th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {(pagamenti || []).map((p) => (
              <tr key={p.id} className="hover:bg-slate-50" data-testid={`pagamento-${p.id}`}>
                <td className="py-2">
                  <span className="px-2 py-0.5 rounded-full text-[11px] bg-slate-100 capitalize">
                    {tipoLabel[p.tipo] || p.tipo}
                  </span>
                </td>
                <td className="py-2 text-right font-mono">{formatEUR(p.importo)}</td>
                <td className="py-2 font-mono text-[12px] text-slate-600">{p.data || "—"}</td>
                <td className="py-2 capitalize">{p.modalita}</td>
                <td className="py-2 text-slate-500 text-[12px]">{p.note || "—"}</td>
                {!readOnly && (
                  <td className="py-2 text-right">
                    <button onClick={() => onEdit(p)} className="text-slate-400 hover:text-blue-600 p-1" title="Modifica">
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button onClick={() => onDelete(p)} className="text-slate-400 hover:text-rose-600 p-1" title="Elimina">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                )}
              </tr>
            ))}
            <tr className="font-semibold text-[12px]">
              <td colSpan={readOnly ? 5 : 6} className="pt-3">
                <div className="flex justify-between">
                  <span>Caparra:</span>
                  <span className="font-mono">{formatEUR(sub("caparra"))}</span>
                </div>
                <div className="flex justify-between">
                  <span>Acconto:</span>
                  <span className="font-mono">{formatEUR(sub("acconto"))}</span>
                </div>
                <div className="flex justify-between">
                  <span>Saldo:</span>
                  <span className="font-mono">{formatEUR(sub("saldo"))}</span>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      )}
    </Card>
  );
}

// -------- Popup edit Venditore --------
function VenditorePopup({ open, onOpenChange, initial, users, onSave }) {
  const [val, setVal] = useState(initial || "");
  useEffect(() => { setVal(initial || ""); }, [initial, open]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[440px]" data-testid="venditore-popup">
        <DialogHeader>
          <DialogTitle>Cambia venditore</DialogTitle>
          <DialogDescription>Solo developer/admin. Tracciato in audit log.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 py-3">
          <Label>Venditore</Label>
          <Select value={val} onValueChange={setVal}>
            <SelectTrigger data-testid="venditore-select"><SelectValue placeholder="Seleziona venditore" /></SelectTrigger>
            <SelectContent>
              {users.map((u) => (
                <SelectItem key={u.id} value={u.id}>
                  {[u.first_name, u.last_name].filter(Boolean).join(" ") || u.email}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button onClick={() => onSave(val)} data-testid="venditore-save">Salva</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Sprint 4 — la data contratto si assegna da sola: quella già presente, altrimenti oggi.
function DataContrattoAuto({ v, testid }) {
  const d = v?.data_contratto ? String(v.data_contratto).slice(0, 10) : new Date().toISOString().slice(0, 10);
  return (
    <div className="text-[13px] text-slate-700" data-testid={testid}>
      <span className="text-[12px] uppercase text-slate-500">Data contratto: </span>
      <b>{d.split("-").reverse().join("/")}</b>
      <span className="text-slate-500"> — {v?.data_contratto ? "già assegnata" : "assegnata automaticamente con la data di oggi"}</span>
    </div>
  );
}

// -------- Pulsante CONFERMA IN VENDITA (Sprint 4A.4 B3+B4) --------
function ConfermaVenditaButton({ vendita, vehicle, margine, isAdmin, canWorkflow, onDone }) {
  const [open, setOpen] = useState(false);
  const [forceMotivo, setForceMotivo] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (!vendita || !vehicle) return null;
  if (vendita.status !== "impegnata") return null;
  // Sprint 4X.3: il modello 2-assi non richiede più che il veicolo sia in stato specifico.
  // Il bottone è visibile per ogni vendita.impegnata, indipendentemente dalla disponibilità fisica.
  if (!canWorkflow) return null;

  const mNetto = typeof margine?.margine_netto === "number" ? margine.margine_netto : null;
  const mMin = typeof margine?.margine_minimo === "number" ? margine.margine_minimo : null;
  const sottoMargine = (mNetto !== null && mMin !== null) ? mNetto < mMin : false;
  // Non-admin: pulsante disabilitato se sotto margine. Admin: sempre abilitato (ma vedrà modale forzata).
  // Sprint 3: margine non determinabile (carico mancante) → bloccato per tutti.
  const nonDeterminabile = margine?.semaforo === "non_determinabile";
  const disabled = nonDeterminabile || (sottoMargine && !isAdmin);

  const submit = async () => {
    setSubmitting(true);
    try {
      // Sprint 4X.3 (Bug critico fix): usa endpoint asse-2 commerciale, non asse-1 fisica.
      const payload = {
        target_stato: "venduta",
      };
      if (sottoMargine && isAdmin) {
        payload.force_sotto_margine = true;
        payload.force_motivo = forceMotivo.trim();
      }
      await api.post(`/vendite/${vendita.id}/transition_commerciale`, { ...payload, stato_partenza: vendita.status });
      toast.success("Vendita confermata: pratica passata a VENDUTA");
      setOpen(false);
      onDone?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore nella conferma vendita");
    } finally {
      setSubmitting(false);
    }
  };

  const motivoValid = forceMotivo.trim().length >= 10;
  const canSubmit = !submitting && (sottoMargine && isAdmin ? motivoValid : true);

  return (
    <>
      <Button
        size="sm"
        onClick={() => setOpen(true)}
        disabled={disabled}
        className={`bg-emerald-600 hover:bg-emerald-700 text-white font-semibold ${disabled ? "opacity-60" : ""}`}
        data-testid="btn-conferma-in-vendita"
        title={nonDeterminabile ? "Margine non determinabile — tipo di carico mancante: impostarlo nella scheda Carico e importi"
          : disabled ? "Margine sotto soglia: aumentare prezzo o ridurre sconti" : "Conferma vendita (passa a VENDUTA)"}
      >
        <CheckCircle2 className="h-4 w-4 mr-1.5" />
        CONFERMA IN VENDITA
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="conferma-vendita-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {sottoMargine ? (
                <><AlertTriangle className="h-5 w-5 text-amber-600" /> Conferma vendita FORZATA</>
              ) : (
                <><CheckCircle2 className="h-5 w-5 text-emerald-600" /> Conferma in VENDITA</>
              )}
            </DialogTitle>
            <DialogDescription>
              La pratica passerà a <b>VENDUTA</b> e il veicolo a <b>SOLD</b>.
            </DialogDescription>
          </DialogHeader>

          {sottoMargine && (
            <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-[13px] text-amber-900">
              <b>Margine sotto soglia.</b> Netto:{" "}
              <span className="font-mono">{formatEUR(mNetto)}</span> · Minimo:{" "}
              <span className="font-mono">{formatEUR(mMin)}</span>.
              <br />
              Solo developer/admin possono forzare. Inserire un motivo (≥ 10 caratteri) per l'audit log.
            </div>
          )}

          <div className="space-y-3 py-2">
            <DataContrattoAuto v={vendita} testid="conferma-vendita-data-contratto" />

            {sottoMargine && isAdmin && (
              <div>
                <Label className="text-[12px] uppercase text-slate-500">Motivo forzatura (min 10 caratteri) *</Label>
                <Textarea
                  value={forceMotivo}
                  onChange={(e) => setForceMotivo(e.target.value)}
                  rows={3}
                  placeholder="Motivazione commerciale per forzare la vendita sotto margine..."
                  data-testid="conferma-vendita-motivo"
                />
                <div className={`text-[11px] mt-1 ${motivoValid ? "text-emerald-600" : "text-slate-400"}`}>
                  {forceMotivo.trim().length}/10 caratteri
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={submitting}>
              Annulla
            </Button>
            <Button
              onClick={submit}
              disabled={!canSubmit}
              className={sottoMargine ? "bg-amber-600 hover:bg-amber-700 text-white" : "bg-emerald-600 hover:bg-emerald-700 text-white"}
              data-testid="conferma-vendita-submit"
            >
              {submitting ? "Invio..." : (sottoMargine ? "Conferma forzata" : "Conferma")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// -------- Sprint 4B.1 (A2): WorkflowFlowBar --------
// Pulsanti regredisci/avanza per pratica in stato impegnata/venduta/consegnata.
function WorkflowFlowBar({ vendita, vehicle, margine, isAdmin, canWorkflow, canUnlock, onDone }) {
  const [openPopup, setOpenPopup] = useState(null); // 'reserved-to-avail' | 'sold-back' | 'delivered-back' | 'venduta' | 'consegnata'
  const [motivo, setMotivo] = useState("");
  const [dataConsegna, setDataConsegna] = useState(new Date().toISOString().slice(0, 10));
  const [forceMotivo, setForceMotivo] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // Sprint PERMUTA.4 — scelta esplicita su veicolo-permuta durante annullamento
  const [permutaAction, setPermutaAction] = useState(null); // null | "tieni" | "cestino"
  // Sprint 4F2-CVT.C — blocco passaggio "Consegnata" se polizza CVT esiste senza data_decorrenza
  const [cvtBlockOpen, setCvtBlockOpen] = useState(false);

  const handleConsegnataClick = async () => {
    try {
      const r = await api.get(`/vendite/${vendita.id}/polizza-cvt`);
      if (r?.data && !r.data.data_decorrenza) {
        setCvtBlockOpen(true);
        return;
      }
    } catch (e) {
      // 404 = no polizza, ok proseguire
    }
    setOpenPopup("consegnata");
  };

  if (!vendita || !vehicle) return null;
  const vstatus = vendita.status;
  const vehStatus = vehicle.status;
  // Mostra solo per pratica in workflow
  if (!["impegnata", "venduta", "consegnata"].includes(vstatus)) return null;

  const mNetto = typeof margine?.margine_netto === "number" ? margine.margine_netto : null;
  const mMin = typeof margine?.margine_minimo === "number" ? margine.margine_minimo : null;
  const sottoMargine = (mNetto !== null && mMin !== null) ? mNetto < mMin : false;

  const close = () => { setOpenPopup(null); setMotivo(""); setForceMotivo(""); setPermutaAction(null); };



  // Sprint 4X.1 (BM1): annulla impegno tramite endpoint asse-2 — non tocca vehicle
  // Sprint PERMUTA.4: se la permuta è materializzata (vehicle_id valorizzato),
  // l'utente DEVE scegliere fra TIENI IN STOCK o CESTINO per il veicolo-permuta.
  const doAnnullaImpegno = async (motivoTxt) => {
    setSubmitting(true);
    try {
      const payload = { target_stato: "annullata", motivo: motivoTxt };
      if (vendita?.permuta?.vehicle_id) {
        payload.permuta_action = permutaAction;
      }
      await api.post(`/vendite/${vendita.id}/transition_commerciale`, { ...payload, stato_partenza: vendita.status });
      if (vendita?.permuta?.vehicle_id) {
        toast.success(
          permutaAction === "cestino"
            ? "Pratica annullata — veicolo-permuta spostato nel cestino"
            : "Pratica annullata — veicolo-permuta mantenuto in stock"
        );
      } else {
        toast.success("Impegno annullato — disponibilità fisica invariata");
      }
      close();
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore annullamento");
    } finally {
      setSubmitting(false);
    }
  };

  // Sprint 4X.3 (Bug critico): tutte le transizioni commerciali asse-2 vanno
  // attraverso /vendite/{id}/transition_commerciale, NON via /vehicles/{id}/transition
  // (che è asse-1 fisica). Questo evita l'errore "INCOMING → SOLD" su veicoli non AVAILABLE.
  const doCommerciale = async (target_stato, extra = {}) => {
    setSubmitting(true);
    try {
      await api.post(`/vendite/${vendita.id}/transition_commerciale`, {
        stato_partenza: vendita.status,
        target_stato, ...extra,
      });
      toast.success("Stato commerciale aggiornato");
      close();
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore transizione commerciale");
    } finally {
      setSubmitting(false);
    }
  };

  // Permessi UI (server-side resta autoritativo)
  const isOwner = vendita.venditore_id && (vendita.venditore_id === window.__currentUserId);
  // Non possiamo conoscere `agire_su_pratica_*` precisi qui senza altri dati;
  // disabilitiamo solo se canWorkflow è false e non è isAdmin
  const canRegress = canWorkflow || isAdmin || isOwner;

  // Decidi i 2 pulsanti in base allo status
  let LeftBtn = null, RightBtn = null;
  if (vstatus === "impegnata") {
    LeftBtn = (
      <Button
        size="sm" variant="outline"
        className="border-rose-400 text-rose-700 hover:bg-rose-50"
        onClick={() => setOpenPopup("reserved-to-avail")}
        disabled={!canRegress}
        title={canRegress ? "Annulla l'impegno (la disponibilità fisica del veicolo non cambia)"
          : "Permesso richiesto: venditore della pratica o 'Agisci su pratica venduta'"}
        data-testid="flow-btn-annulla-impegno"
      >
        Annulla impegno
      </Button>
    );
    const advanceDisabled = sottoMargine && !isAdmin;
    RightBtn = (
      <Button
        size="sm"
        className={`bg-rose-600 hover:bg-rose-700 text-white ${advanceDisabled ? "opacity-60" : ""}`}
        onClick={() => setOpenPopup("venduta")}
        disabled={advanceDisabled || !canWorkflow}
        title={advanceDisabled ? "Margine sotto soglia: solo admin può forzare"
          : "Conferma in vendita (passa a VENDUTA)"}
        data-testid="flow-btn-fwd-venduta"
      >
        Venduta ›
      </Button>
    );
  } else if (vstatus === "venduta") {
    LeftBtn = (
      <Button
        size="sm" variant="outline"
        className="border-amber-400 text-amber-700 hover:bg-amber-50"
        onClick={() => setOpenPopup("sold-back")}
        disabled={!isAdmin}
        title={isAdmin ? "Regredisci a Impegnata"
          : "Permesso richiesto: 'Agisci su pratica venduta'"}
        data-testid="flow-btn-back-impegnata"
      >
        ‹ Impegnata
      </Button>
    );
    // Sprint 4X: vincolo bloccante — Consegna richiede vehicle.disponibilita_fisica = AVAILABLE
    const dispFisica = vehicle?.disponibilita_fisica || vehicle?.status;
    const consegnaBlocked = dispFisica !== "AVAILABLE";
    RightBtn = (
      <Button
        size="sm"
        className={`bg-violet-600 hover:bg-violet-700 text-white ${consegnaBlocked ? "opacity-60" : ""}`}
        onClick={handleConsegnataClick}
        disabled={!canWorkflow || consegnaBlocked}
        title={consegnaBlocked
          ? `Veicolo non ancora disponibile fisicamente (attualmente: ${dispFisica}): cambia disponibilità del veicolo prima`
          : "Conferma consegna (passa a CONSEGNATA)"}
        data-testid="flow-btn-fwd-consegnata"
      >
        Consegnata ›
      </Button>
    );
  } else if (vstatus === "consegnata") {
    // Sprint VEND-UNLOCK — regressione ‹ Venduta richiede TUTTI questi:
    // 1) permesso `agire_su_pratica_consegnata` (codificato in `canUnlock` lato parent)
    // 2) sblocco temporaneo esplicito attivo (incluso anche per admin, regola b1)
    // Il parent passa già `canUnlock` = `(canUnlock_perm && isTempUnlocked)`
    // per pratiche consegnate, quindi qui basta usare `canUnlock`.
    const allowed = !!canUnlock;
    LeftBtn = (
      <Button
        size="sm" variant="outline"
        className="border-rose-400 text-rose-700 hover:bg-rose-50"
        onClick={() => setOpenPopup("delivered-back")}
        disabled={!allowed}
        title={allowed ? "Regredisci a Venduta"
          : "Per regredire serve il permesso 'Agisci su pratica consegnata' e lo sblocco temporaneo attivo"}
        data-testid="flow-btn-back-venduta"
      >
        ‹ Venduta
      </Button>
    );
    // Nessun avanzamento da consegnata
  }

  return (
    <div className="inline-flex items-center gap-2" data-testid="workflow-flow-bar">
      {LeftBtn}
      {RightBtn}

      {/* Sprint 4F2-CVT.C — Modal BLOCCANTE: data_decorrenza polizza CVT mancante */}
      <Dialog open={cvtBlockOpen} onOpenChange={setCvtBlockOpen}>
        <DialogContent data-testid="cvt-block-modal" onPointerDownOutside={(e) => e.preventDefault()}
                       onEscapeKeyDown={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle>Data decorrenza polizza CVT obbligatoria</DialogTitle>
            <DialogDescription>
              Per passare a <b>Consegnata</b> è necessario impostare la data
              di decorrenza della polizza CVT.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCvtBlockOpen(false)}
                    data-testid="cvt-block-cancel">Annulla</Button>
            <Button onClick={() => {
              setCvtBlockOpen(false);
              // Apre la scheda polizza (scroll to card + emit custom event)
              document.querySelector('[data-testid="card-polizza-cvt"]')?.scrollIntoView({ behavior: "smooth" });
              const btn = document.querySelector('[data-testid="btn-edit-cvt"]');
              if (btn) setTimeout(() => btn.click(), 400);
            }} data-testid="cvt-block-open-polizza">
              Apri scheda polizza
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sprint 4X.1 (BM1): Annulla impegno (NON tocca disponibilità fisica)
          Sprint PERMUTA.4: se la permuta è materializzata, richiede scelta TIENI/CESTINO */}
      <Dialog open={openPopup === "reserved-to-avail"} onOpenChange={(o) => !o && close()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Annullare l'impegno?</DialogTitle>
            <DialogDescription>
              La pratica diventerà <b>annullata</b> e il cliente verrà rimosso.
              <br />
              <span className="text-emerald-700">
                La disponibilità fisica del veicolo (<b>{vehicle?.disponibilita_fisica || vehicle?.status}</b>) <b>non viene modificata</b>.
              </span>
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label>Motivo *</Label>
            <Textarea rows={3} value={motivo} onChange={(e) => setMotivo(e.target.value)}
              placeholder="Almeno 10 caratteri" data-testid="flow-annulla-motivo" />
          </div>
          {vendita?.permuta?.vehicle_id && (
            <div className="rounded-md border border-amber-300 bg-amber-50 p-3 space-y-2"
                 data-testid="flow-annulla-permuta-block">
              <div className="text-sm font-medium text-amber-900">
                Permuta materializzata
              </div>
              <div className="text-xs text-amber-800">
                Questa pratica ha un veicolo-permuta già a magazzino
                (<span className="font-mono">{vendita.permuta.veicolo_descrizione || vendita.permuta.vehicle_id}</span>).
                Scegli cosa farne:
              </div>
              <RadioGroup
                value={permutaAction || ""}
                onValueChange={setPermutaAction}
                className="gap-2"
              >
                <label className="flex items-start gap-2 cursor-pointer"
                       data-testid="flow-annulla-permuta-tieni">
                  <RadioGroupItem value="tieni" id="permaction-tieni" className="mt-0.5" />
                  <div className="text-sm">
                    <div className="font-medium">Tieni in stock</div>
                    <div className="text-xs text-slate-600">
                      Il veicolo-permuta resta in magazzino, scollegato dalla pratica.
                    </div>
                  </div>
                </label>
                <label className="flex items-start gap-2 cursor-pointer"
                       data-testid="flow-annulla-permuta-cestino">
                  <RadioGroupItem value="cestino" id="permaction-cestino" className="mt-0.5" />
                  <div className="text-sm">
                    <div className="font-medium">Cestino</div>
                    <div className="text-xs text-slate-600">
                      Il veicolo-permuta viene spostato nel cestino (soft-delete).
                    </div>
                  </div>
                </label>
              </RadioGroup>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={close} disabled={submitting}>Indietro</Button>
            <Button
              className="bg-rose-600 hover:bg-rose-700 text-white"
              disabled={
                submitting ||
                motivo.trim().length < 10 ||
                (!!vendita?.permuta?.vehicle_id && !permutaAction)
              }
              onClick={() => doAnnullaImpegno(motivo.trim())}
              data-testid="flow-annulla-submit"
            >
              {submitting ? "..." : "Annulla impegno"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modale regressione SOLD→RESERVED */}
      <Dialog open={openPopup === "sold-back"} onOpenChange={(o) => !o && close()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Regredire a Impegnata?</DialogTitle>
            <DialogDescription>
              La pratica tornerà a <b>IMPEGNATA</b> e il veicolo a <b>RESERVED</b>.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label>Motivo *</Label>
            <Textarea rows={3} value={motivo} onChange={(e) => setMotivo(e.target.value)}
              placeholder="Almeno 10 caratteri" data-testid="flow-back-motivo-sold" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={close} disabled={submitting}>Annulla</Button>
            <Button
              className="bg-amber-500 hover:bg-amber-600 text-white"
              disabled={submitting || motivo.trim().length < 10}
              onClick={() => doCommerciale("impegnata", { motivo: motivo.trim() })}
              data-testid="flow-back-sold-submit"
            >
              {submitting ? "..." : "Regredisci a Impegnata"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modale regressione DELIVERED→SOLD */}
      <Dialog open={openPopup === "delivered-back"} onOpenChange={(o) => !o && close()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Regredire a Venduta?</DialogTitle>
            <DialogDescription>
              La pratica tornerà a <b>VENDUTA</b> e il veicolo a <b>SOLD</b>.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label>Motivo *</Label>
            <Textarea rows={3} value={motivo} onChange={(e) => setMotivo(e.target.value)}
              placeholder="Almeno 10 caratteri" data-testid="flow-back-motivo-delivered" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={close} disabled={submitting}>Annulla</Button>
            <Button
              className="bg-rose-600 hover:bg-rose-700 text-white"
              disabled={submitting || motivo.trim().length < 10}
              onClick={() => doCommerciale("venduta", { motivo: motivo.trim() })}
              data-testid="flow-back-delivered-submit"
            >
              {submitting ? "..." : "Regredisci a Venduta"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modale RESERVED→SOLD (avanza a venduta) — riusa logica conferma vendita */}
      <Dialog open={openPopup === "venduta"} onOpenChange={(o) => !o && close()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {sottoMargine && isAdmin
                ? <><AlertTriangle className="h-5 w-5 text-amber-600" /> Conferma vendita FORZATA</>
                : <><CheckCircle2 className="h-5 w-5 text-rose-600" /> Conferma in VENDUTA</>}
            </DialogTitle>
            <DialogDescription>La pratica passerà a <b>VENDUTA</b> e il veicolo a <b>SOLD</b>.</DialogDescription>
          </DialogHeader>
          {sottoMargine && isAdmin && (
            <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-[13px] text-amber-900">
              <b>Margine sotto soglia.</b> Netto: <span className="font-mono">{formatEUR(mNetto)}</span> · Minimo: <span className="font-mono">{formatEUR(mMin)}</span>. Inserire un motivo (≥ 10 caratteri).
            </div>
          )}
          <div className="space-y-3 py-2">
            <DataContrattoAuto v={vendita} testid="flow-fwd-venduta-data" />
            {sottoMargine && isAdmin && (
              <div>
                <Label>Motivo forzatura *</Label>
                <Textarea rows={2} value={forceMotivo} onChange={(e) => setForceMotivo(e.target.value)}
                  placeholder="Almeno 10 caratteri" data-testid="flow-fwd-venduta-motivo" />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={close} disabled={submitting}>Annulla</Button>
            <Button
              className="bg-rose-600 hover:bg-rose-700 text-white"
              disabled={submitting || (sottoMargine && isAdmin && forceMotivo.trim().length < 10)}
              onClick={() => doCommerciale("venduta", {
                ...(sottoMargine && isAdmin
                  ? { force_sotto_margine: true, force_motivo: forceMotivo.trim() }
                  : {}),
              })}
              data-testid="flow-fwd-venduta-submit"
            >
              {submitting ? "..." : "Conferma vendita"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modale SOLD→DELIVERED (avanza a consegnata) */}
      <Dialog open={openPopup === "consegnata"} onOpenChange={(o) => !o && close()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Conferma consegna</DialogTitle>
            <DialogDescription>La pratica passerà a <b>CONSEGNATA</b> e il veicolo a <b>DELIVERED</b>.</DialogDescription>
          </DialogHeader>
          <div>
            <Label>Data consegna effettiva *</Label>
            <Input type="date" value={dataConsegna} onChange={(e) => setDataConsegna(e.target.value)}
              data-testid="flow-fwd-consegnata-data" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={close} disabled={submitting}>Annulla</Button>
            <Button
              className="bg-violet-600 hover:bg-violet-700 text-white"
              disabled={submitting || !dataConsegna}
              onClick={() => doCommerciale("consegnata", { data: dataConsegna })}
              data-testid="flow-fwd-consegnata-submit"
            >
              {submitting ? "..." : "Conferma consegna"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// -------- Sprint 4B: PreventivoActionsPanel --------
// Sprint 4X B-FIX-1: supporta `compact` per render solo bottoni in barra superiore.
export function PreventivoActionsPanel({ vendita, isAdmin, onDone, compact = false, onScegliCliente }) {
  const navigate = useNavigate();
  // Sprint CRM-4 — senza cliente «Conferma in vendita» apre sempre la finestra Cliente (voci CRM solo con opportunità collegata)
  const apriCliente = !vendita.cliente_id && onScegliCliente;
  const [confermaOpen, setConfermaOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [cliente, setCliente] = useState(null);
  const [margine, setMargine] = useState(null);

  // Sprint 4B.1 (B4): carica cliente reale + margine per popup conferma
  useEffect(() => {
    let aborted = false;
    (async () => {
      if (vendita?.cliente_id) {
        try {
          const r = await api.get(`/anagrafiche/${vendita.cliente_id}`);
          if (!aborted) setCliente(r.data);
        } catch { /* silent */ }
      }
      try {
        const r = await api.get(`/vendite/${vendita.id}/margine`);
        if (!aborted) setMargine(r.data);
      } catch { /* silent */ }
    })();
    return () => { aborted = true; };
  }, [vendita?.cliente_id, vendita?.id]);

  const clienteName = cliente
    ? (cliente.ragione_sociale
      || `${cliente.nome || ""} ${cliente.cognome || ""}`.trim()
      || cliente.codice_fiscale || cliente.email || "—")
    : "—";
  const totaleVenditaFinale = margine?.totale_vendita_finale
    ?? margine?.totale_vendita
    ?? null;  // Sprint 4X.1 Bug 2A: niente fallback a prezzo_vendita_veicolo (escludeva servizi/accessori)
  const margineLoaded = margine !== null;
  const status = vendita.status;
  const isConfermato = status === "confermato";
  const isScaduto = status === "scaduto";
  const isAnnullato = status === "annullato";
  const dataScad = vendita.data_scadenza_preventivo || vendita.data_scadenza;
  const giorniValid = vendita.giorni_validita_preventivo || 30;
  const giorniRim = (() => {
    if (!dataScad) return null;
    const d = new Date(dataScad);
    const now = new Date();
    return Math.ceil((d - now) / (1000 * 60 * 60 * 24));
  })();

  const fmtDate = (s) => {
    if (!s) return "—";
    try {
      const d = new Date(s);
      const pad = (n) => String(n).padStart(2, "0");
      return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
    } catch { return s; }
  };

  const conferma = async () => {
    setSubmitting(true);
    try {
      const r = await api.post(`/vendite/${vendita.id}/conferma`, { stato_partenza: vendita.status });
      const pratica = r.data;
      toast.success(`Preventivo confermato. Pratica ${pratica.codice} creata.`);
      // Naviga alla pratica appena creata
      if (pratica.vehicle_id) {
        navigate(`/vehicles/${pratica.vehicle_id}?tab=vendita`);
      } else {
        onDone?.();
      }
    } catch (e) {
      toast.error(extractError(e) || "Errore conferma preventivo");
    } finally {
      setSubmitting(false);
      setConfermaOpen(false);
    }
  };

  const duplica = async () => {
    if (!window.confirm("Duplicare questo preventivo? Sarà creata una copia indipendente in stato bozza.")) return;
    setSubmitting(true);
    try {
      const r = await api.post(`/vendite/${vendita.id}/duplica`);
      const dup = r.data;
      toast.success(`Preventivo duplicato: ${dup.codice}`);
      navigate(`/vehicles/${dup.vehicle_id}?tab=preventivi&preventivo_id=${dup.id}`);
    } catch (e) {
      toast.error(extractError(e) || "Errore duplica");
    } finally {
      setSubmitting(false);
    }
  };

  const riapri = async () => {
    if (!window.confirm("Riaprire il preventivo scaduto? La data di scadenza sarà ricalcolata da oggi.")) return;
    setSubmitting(true);
    try {
      await api.post(`/vendite/${vendita.id}/riapri`, { stato_partenza: vendita.status });
      toast.success("Preventivo riaperto");
      onDone?.();
    } catch (e) {
      toast.error(extractError(e) || "Errore riapertura");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {!compact && (
        <>
          <BandaSezione titolo="Preventivo" />
          <Card className="p-4" data-testid="preventivo-actions-panel">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div>
                <Label className="text-[11px] uppercase tracking-wide text-slate-500">Giorni validità</Label>
                <div className="text-2xl font-heading text-slate-900">{giorniValid}</div>
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wide text-slate-500">Data scadenza</Label>
                <div className="text-lg text-slate-900 flex items-center gap-1.5">
                  <CalendarIcon className="h-4 w-4 text-slate-500" />
                  {fmtDate(dataScad)}
                </div>
                {giorniRim !== null && !isConfermato && !isAnnullato && (
                  <div className={`text-xs mt-0.5 ${
                    giorniRim < 0 ? "text-rose-700" :
                    giorniRim <= 3 ? "text-amber-700" : "text-slate-500"
                  }`}>
                    {giorniRim < 0 ? `Scaduto da ${-giorniRim} giorni`
                      : giorniRim === 0 ? "Scade oggi"
                      : `${giorniRim} giorn${giorniRim === 1 ? "o" : "i"} rimanent${giorniRim === 1 ? "e" : "i"}`}
                  </div>
                )}
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wide text-slate-500">Stato</Label>
                <div className={`inline-block px-2 py-0.5 rounded-full text-sm font-medium ${STATUS_BADGE_CLASS[status]}`}>
                  {STATUS_LABELS[status] || status}
                </div>
              </div>
            </div>

            {isConfermato && vendita.pratica_generata_id && (
              <div className="mb-3 p-3 rounded-md bg-emerald-50 border border-emerald-200 text-sm flex items-start gap-2" data-testid="preventivo-confermato-banner">
                <CheckCircle2 className="h-4 w-4 text-emerald-700 mt-0.5" />
                <div>
                  <div className="font-medium text-emerald-900">Preventivo confermato in pratica</div>
                  <div className="text-emerald-800 text-xs mt-0.5">
                    <button
                      type="button"
                      className="underline hover:text-emerald-900"
                      onClick={() => navigate(`/vendite/${vendita.pratica_generata_id}`)}
                      data-testid="preventivo-link-pratica"
                    >
                      Apri pratica generata
                    </button>
                  </div>
                </div>
              </div>
            )}
          </Card>
        </>
      )}

      {compact && (
        <div className="inline-flex flex-wrap gap-2" data-testid="preventivo-top-actions">
          {!isConfermato && !isAnnullato && !isScaduto && (
            <Button
              size="sm"
              className="bg-amber-500 hover:bg-amber-600 text-white"
              onClick={() => (apriCliente ? onScegliCliente() : setConfermaOpen(true))}
              disabled={!vendita.cliente_id && !apriCliente}
              title={!vendita.cliente_id
                ? "Imposta un cliente prima di confermare il preventivo in vendita"
                : "Conferma il preventivo in vendita (crea pratica)"}
              data-testid="btn-conferma-preventivo"
            >
              <CheckCircle2 className="h-4 w-4 mr-1.5" /> Conferma in vendita
            </Button>
          )}
          {!isConfermato && (
            <Button size="sm" variant="outline" onClick={duplica}
              disabled={submitting} data-testid="btn-duplica-preventivo">
              <Copy className="h-4 w-4 mr-1.5" /> Duplica
            </Button>
          )}
          {isScaduto && isAdmin && (
            <Button size="sm" variant="outline" onClick={riapri}
              disabled={submitting} data-testid="btn-riapri-preventivo">
              <RotateCcw className="h-4 w-4 mr-1.5" /> Riapri preventivo
            </Button>
          )}
        </div>
      )}

      {/* Modale conferma */}
      <Dialog open={confermaOpen} onOpenChange={setConfermaOpen}>
        <DialogContent data-testid="modal-conferma-preventivo">
          <DialogHeader>
            <DialogTitle>Confermare preventivo {vendita.codice} in pratica?</DialogTitle>
            <DialogDescription>
              Verrà creata una nuova pratica e il veicolo passerà a{" "}
              <span className="font-semibold">IMPEGNATA</span>.
            </DialogDescription>
          </DialogHeader>
          <div className="text-sm text-slate-700 space-y-1">
            <div>Cliente: <span className="font-medium" data-testid="conferma-cliente-name">{clienteName}</span></div>
            {vendita.vehicle_snapshot && (
              <div>
                Veicolo: <span className="font-medium">
                  {[vendita.vehicle_snapshot.marca, vendita.vehicle_snapshot.modello].filter(Boolean).join(" ")}
                  {vendita.vehicle_snapshot.targa ? ` — ${vendita.vehicle_snapshot.targa}` : ""}
                </span>
              </div>
            )}
            {totaleVenditaFinale != null ? (
              <div>Totale vendita: <span className="font-mono font-semibold" data-testid="conferma-totale-vendita">{formatEUR(totaleVenditaFinale)}</span></div>
            ) : (
              <div className="text-amber-700 text-xs">Caricamento totale in corso...</div>
            )}
            <div className="text-xs text-slate-500 pt-1">
              La pratica creata sarà <span className="font-mono">PRAT-{new Date().getFullYear()}-XXXX</span> e il veicolo passerà a <b>IMPEGNATA</b>.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfermaOpen(false)} disabled={submitting} data-testid="btn-conferma-cancel">
              Annulla
            </Button>
            <Button
              className="bg-amber-500 hover:bg-amber-600 text-white"
              onClick={conferma}
              disabled={submitting || !margineLoaded}
              title={margineLoaded ? "" : "Caricamento totale in corso..."}
              data-testid="btn-conferma-submit"
            >
              {submitting ? "Conversione..." : "Conferma e crea pratica"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// -------- Sprint 4B.2 (B10): selettore prezzo vendita (3 stati) --------
function PrezzoVenditaSelector({ vendita, snap, margine, canEdit, isLocked, onChangeScelta }) {
  const scelta = vendita.prezzo_vendita_scelta || "web";
  const prezzoWeb = Number(snap?.prezzo_web || 0);
  // Sprint FIX-MONITOR — in modalità Monitor Cliente i singoli costi (acquisto,
  // spese, margine min) sono masked dal backend; usiamo il precomputed
  // `prezzo_vendita_calcolato` esposto dal backend (è un prezzo di VENDITA,
  // visibile al cliente). Fallback al calcolo client per retro-compatibilità.
  const prezzoVendita = snap?.prezzo_vendita_calcolato != null
    ? Number(snap.prezzo_vendita_calcolato)
    : Number(snap?.prezzo_acquisto || 0)
      + Number(snap?.totale_spese_generiche || 0)
      + Number(snap?.totale_spese_ripristino || 0)
      + Number(snap?.margine_minimo || 0);
  const valoreCorrente = margine?.prezzo_vendita_veicolo_effettivo;

  const opzioni = [
    { v: "web", label: "Prezzo web", valore: prezzoWeb,
      hint: "Da scheda Importi del veicolo" },
    { v: "vendita", label: "Prezzo vendita", valore: prezzoVendita,
      hint: "Acquisto + spese + margine min." },
    { v: "manuale", label: "Manuale", valore: vendita.prezzo_vendita_veicolo,
      hint: "Valore inserito manualmente" },
  ];

  const disabled = !canEdit || isLocked;

  return (
    <div className="flex flex-wrap items-center gap-1.5 mb-2 text-[12px]" data-testid="prezzo-vendita-selector">
      <span className="text-[11px] uppercase text-slate-500 mr-1">Prezzo vendita:</span>
      {opzioni.map((o) => {
        const active = scelta === o.v;
        return (
          <button
            key={o.v}
            type="button"
            disabled={disabled}
            onClick={() => !disabled && onChangeScelta(o.v)}
            title={o.hint}
            data-testid={`prezzo-scelta-${o.v}`}
            data-active={active ? "true" : "false"}
            className={`px-2.5 py-1 rounded-md border text-[12px] transition-colors
              ${active
                ? "bg-blue-600 text-white border-blue-600"
                : "bg-white text-slate-700 border-slate-300 hover:border-blue-400"}
              ${disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
          >
            <span className="font-medium">{o.label}</span>
            {o.v !== "manuale" && (
              <span className={`ml-1.5 font-mono text-[11px] ${active ? "text-white/80" : "text-slate-500"}`}>
                {formatEUR(o.valore)}
              </span>
            )}
          </button>
        );
      })}
      {scelta !== "manuale" && valoreCorrente != null && (
        <span className="text-[11px] text-slate-500 ml-1">→ <span className="font-mono text-blue-700 font-semibold">{formatEUR(valoreCorrente)}</span></span>
      )}
    </div>
  );
}



// Sprint STAMPE (§92) — Dati di consegna della pratica.
const CONSEGNA_DOCS = [
  ["carta_circolazione", "Carta di circolazione / documento unico"],
  ["certificato_proprieta", "Certificato di proprietà"],
  ["fattura_contratto", "Fattura o contratto"],
  ["certificato_garanzia", "Certificato di garanzia"],
  ["libretto_uso_manutenzione", "Libretto uso e manutenzione"],
  ["tagliandi", "Tagliandi"],
  ["chiavi", "Chiavi"],
];
const MOTIVO_TRATTENUTA_LABELS = {
  passaggio_proprieta: "Passaggio di proprietà",
  aggiornamento: "Aggiornamento del documento",
  rinnovo: "Rinnovo",
  altro: "Altro",
};

// §97 — Data consegna unica: la data e ora del Modulo consegne è la fonte; data_consegna_effettiva ne è il derivato.
function commitDataConsegna(v, patchTop, dc, val) {
  patchTop({ dati_consegna: { ...dc, data_ora_consegna: val || null },
             data_consegna_effettiva: val ? String(val).slice(0, 10) : null });
}

function proposedConsegna(v) {
  return v.data_consegna_effettiva ? `${String(v.data_consegna_effettiva).slice(0, 10)}T00:00` : "";
}

function DatePraticaCard({ v, setV, patchTop, isLocked, canUnlock }) {
  // Sprint 4 — data contratto: libera in impegnata; da VENDUTA serve il permesso e lo sblocco
  // temporaneo del campo. Data e ora di consegna: libere fino alla consegna.
  const [sbloccoData, setSbloccoData] = useState(false);
  const chiusa = ["venduta", "consegnata"].includes(v.status);
  const annullata = v.status === "annullata";
  const contrattoBloccato = annullata || (chiusa && !(canUnlock && sbloccoData));
  const consegnaBloccata = annullata || (v.status === "consegnata" && isLocked);
  const dc = v.dati_consegna && typeof v.dati_consegna === "object" ? v.dati_consegna : {};
  const [dataOra, setDataOra] = useState(dc.data_ora_consegna || proposedConsegna(v));
  useEffect(() => { setDataOra(dc.data_ora_consegna || proposedConsegna(v)); }, [dc.data_ora_consegna, v.data_consegna_effettiva]); // eslint-disable-line
  return (
    <>
      <BandaSezione titolo="Date pratica" />
      <Card className="p-3" data-testid="card-date-pratica">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <Label className="text-xs text-slate-500">Data impegno</Label>
            <Input type="date" data-testid="date-impegno" value={v.data_impegno || ""} disabled={isLocked}
              onChange={(e) => setV({ ...v, data_impegno: e.target.value })}
              onBlur={() => patchTop({ data_impegno: v.data_impegno || null })} />
          </div>
          <div>
            <Label className="text-xs text-slate-500">Data contratto <span className="ml-1 text-[10px] text-amber-600">(sui documenti)</span></Label>
            <Input type="date" data-testid="date-contratto" value={v.data_contratto || ""} disabled={contrattoBloccato}
              onChange={(e) => setV({ ...v, data_contratto: e.target.value })}
              onBlur={() => { if (!contrattoBloccato) { patchTop({ data_contratto: v.data_contratto || null }); setSbloccoData(false); } }} />
            {chiusa && !annullata && canUnlock && !sbloccoData && (
              <button type="button" className="mt-1 text-[11px] text-amber-700 underline underline-offset-2 inline-flex items-center gap-1"
                onClick={() => setSbloccoData(true)} data-testid="date-contratto-sblocca">
                <Unlock className="h-3 w-3" /> Sblocca temporaneamente la data contratto
              </button>
            )}
          </div>
          <div>
            <Label className="text-xs text-slate-500">Data e ora consegna <span className="ml-1 text-[10px] text-amber-600">(sui documenti)</span></Label>
            <Input type="datetime-local" data-testid="date-consegna" value={dataOra} disabled={consegnaBloccata}
              onChange={(e) => setDataOra(e.target.value)}
              onBlur={() => commitDataConsegna(v, patchTop, dc, dataOra)} />
          </div>
        </div>
        <p className="mt-2 text-[11px] text-slate-400" data-testid="date-doc-hint">
          La data contratto si assegna da sola alla generazione del contratto o al passaggio a venduta; da venduta in poi
          si modifica solo con il permesso "{v.status === "consegnata" ? "Agisci su pratica consegnata" : "Agisci su pratica venduta"}" e lo sblocco temporaneo.
          La data e ora di consegna resta libera fino alla consegna.
        </p>
      </Card>
    </>
  );
}

function DatiConsegnaCard({ v, patchTop, disabled }) {
  const base = v.dati_consegna && typeof v.dati_consegna === "object" ? v.dati_consegna : {};
  const [dc, setDc] = useState(base);
  useEffect(() => {
    setDc(v.dati_consegna && typeof v.dati_consegna === "object" ? v.dati_consegna : {});
  }, [v.dati_consegna]);
  const docs = dc.documenti_consegnati && typeof dc.documenti_consegnati === "object" ? dc.documenti_consegnati : {};
  // data_consegna_effettiva (solo data) proposta come default — NON la sovrascrive.
  const proposed = proposedConsegna(v);
  const commit = (next) => { setDc(next); patchTop({ dati_consegna: next }); };
  const setLocal = (patch) => setDc((d) => ({ ...d, ...patch }));
  return (
    <>
      <BandaSezione titolo="Modulo consegne" />
      <Card className="p-3" data-testid="card-dati-consegna">
        <fieldset disabled={disabled} className={disabled ? "opacity-75" : ""}>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <div>
              <Label className="text-xs text-slate-500">Data e ora consegna</Label>
              <Input type="datetime-local" data-testid="consegna-data-ora"
                value={dc.data_ora_consegna || proposed}
                onChange={(e) => setLocal({ data_ora_consegna: e.target.value })}
                onBlur={() => { const val = dc.data_ora_consegna || proposed || null; setDc({ ...dc, data_ora_consegna: val }); commitDataConsegna(v, patchTop, dc, val); }} />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Km alla consegna</Label>
              <Input type="number" data-testid="consegna-km" value={dc.km_consegna ?? ""}
                onChange={(e) => setLocal({ km_consegna: e.target.value === "" ? null : Number(e.target.value) })}
                onBlur={() => commit(dc)} />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Numero chiavi</Label>
              <Input type="number" data-testid="consegna-numero-chiavi" value={dc.numero_chiavi_consegnate ?? ""}
                onChange={(e) => setLocal({ numero_chiavi_consegnate: e.target.value === "" ? null : Number(e.target.value) })}
                onBlur={() => commit(dc)} />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Documento di circolazione trattenuto</Label>
              <Select value={dc.documento_trattenuto ? "si" : "no"}
                onValueChange={(val) => commit({ ...dc, documento_trattenuto: val === "si", ...(val === "no" ? { motivo_trattenuta: null } : {}) })}>
                <SelectTrigger data-testid="consegna-trattenuto"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="no">No</SelectItem>
                  <SelectItem value="si">Sì</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="mt-3">
            <Label className="text-xs text-slate-500">Documenti consegnati</Label>
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-2" data-testid="consegna-documenti-group">
              {CONSEGNA_DOCS.map(([k, label]) => (
                <label key={k} className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer select-none">
                  <Checkbox checked={!!docs[k]} data-testid={`consegna-doc-${k.replace(/_/g, "-")}`}
                    onCheckedChange={(b) => commit({ ...dc, documenti_consegnati: { ...docs, [k]: b === true } })} />
                  <span>{label}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {dc.documento_trattenuto && (
              <div>
                <Label className="text-xs text-slate-500">Motivo della trattenuta</Label>
                <Select value={dc.motivo_trattenuta || ""} onValueChange={(val) => commit({ ...dc, motivo_trattenuta: val })}>
                  <SelectTrigger data-testid="consegna-motivo-trattenuta"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(MOTIVO_TRATTENUTA_LABELS).map(([k, lbl]) => <SelectItem key={k} value={k}>{lbl}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <div className="mt-3 grid grid-cols-1 gap-3">
            <div>
              <Label className="text-xs text-slate-500">Altro</Label>
              <Input data-testid="consegna-altro" value={dc.altro || ""}
                onChange={(e) => setLocal({ altro: e.target.value })} onBlur={() => commit(dc)} />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Note della consegna</Label>
              <Textarea rows={3} data-testid="consegna-note" value={dc.note_consegna || ""}
                onChange={(e) => setLocal({ note_consegna: e.target.value })} onBlur={() => commit(dc)} />
            </div>
          </div>
        </fieldset>
      </Card>
    </>
  );
}


// -------- Sprint 4B.2 (B8): Note con Tabs (Stampabili / Interne) --------
function NoteTabs({ v, setV, patchTop }) {
  return (
    <Tabs defaultValue="stampabili" className="w-full" data-testid="note-tabs">
      <TabsList className="grid w-full grid-cols-2 h-8">
        <TabsTrigger value="stampabili" className="text-xs" data-testid="tab-note-stampabili">Stampabili</TabsTrigger>
        <TabsTrigger value="interne" className="text-xs" data-testid="tab-note-interne">Interne</TabsTrigger>
      </TabsList>
      <TabsContent value="stampabili" className="mt-2">
        <Textarea rows={3} value={v.note_pratica || ""}
          placeholder="Note contratto (in stampa)"
          onChange={(e) => setV({ ...v, note_pratica: e.target.value })}
          onBlur={() => patchTop({ note_pratica: v.note_pratica })}
          data-testid="note-stampabili-textarea" />
      </TabsContent>
      <TabsContent value="interne" className="mt-2">
        <Textarea rows={3} value={v.note_interne || ""}
          placeholder="Note interne (NON in stampa)"
          onChange={(e) => setV({ ...v, note_interne: e.target.value })}
          onBlur={() => patchTop({ note_interne: v.note_interne })}
          data-testid="note-interne-textarea" />
      </TabsContent>
    </Tabs>
  );
}

// -------- COMPONENT MAIN (embeddable) --------
export default function VenditaDetailContent({ venditaId, onLoaded, hideHeader = false, onGoTo }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [v, setV] = useState(null);
  const [vehicle, setVehicle] = useState(null);
  // Sprint 4I1.B2.C — codice IVA default azienda (per sconti generici)
  const [companyIvaDefault, setCompanyIvaDefault] = useState("I22");
  // Sprint 4F1.3: cache lookup nome finanziaria per la Card
  const [finanziariaName, setFinanziariaName] = useState(null);

  const [rigaPopup, setRigaPopup] = useState({ open: false, categoria: "servizi", initial: null });
  const [finPopup, setFinPopup] = useState(false);
  const [permPopup, setPermPopup] = useState(false);
  const [permVehiclePickerOpen, setPermVehiclePickerOpen] = useState(false);
  const [permVehiclePickerCallback, setPermVehiclePickerCallback] = useState(null);
  const [capPopup, setCapPopup] = useState(false);
  const [passPopup, setPassPopup] = useState(false);
  const [agenziaPassName, setAgenziaPassName] = useState(null);
  const [cambioOpen, setCambioOpen] = useState(false);
  // Sprint VEND-UNLOCK: sblocco temporaneo esplicito per pratica CONSEGNATA.
  // Anche con permesso `agire_su_pratica_consegnata`, la pratica resta
  // protetta finché l'operatore non clicca "Sblocca temporaneamente".
  // Il blocco viene ripristinato automaticamente al primo salvataggio.
  const [isTempUnlocked, setIsTempUnlocked] = useState(false);
  // Sprint PRATICHE-CONCORRENZA — punti di partenza delle finestre aperte ed esito dei conflitti
  const aperte = useRef({});
  const [esitoVoce, setEsitoVoce] = useState(null); // { esito, rotta, voce, mieiValori }

  // Sprint SICUREZZA-AMBITO — regola unica (lib/ambito.js), sull'azienda×sede della vendita
  // Sprint SICUREZZA-AMBITO (R1) — la pratica segue l'azienda×sede del veicolo, risolte dal backend
  const ambitoVendita = { cid: v?.ambito?.company_id, sid: v?.ambito?.site_id };
  const canEconomici = useMemo(() => haFlag(user, "gestione_incassi", ambitoVendita.cid, ambitoVendita.sid),
    [user, ambitoVendita.cid, ambitoVendita.sid]);

  const isAdmin = useMemo(() => ruoloGestione(user, ambitoVendita.cid), [user, ambitoVendita.cid]);

  const canUnlock = useMemo(() => haFlag(user,
    v?.status === "consegnata" ? "agire_su_pratica_consegnata" : "agire_su_pratica_venduta",
    ambitoVendita.cid, ambitoVendita.sid), [user, v?.status, ambitoVendita.cid, ambitoVendita.sid]);


  // Sprint 4F1.3: fetch nome finanziaria per la Card
  useEffect(() => {
    const fid = v?.finanziamento?.finanziaria_id;
    if (!fid) { setFinanziariaName(null); return; }
    api.get(`/anagrafiche/${fid}`).then((r) => {
      const a = r.data;
      setFinanziariaName(a.tipo_persona === "azienda"
        ? a.ragione_sociale
        : [a.cognome, a.nome].filter(Boolean).join(" "));
    }).catch(() => setFinanziariaName(null));
  }, [v?.finanziamento?.finanziaria_id]);

  // Sprint 4F2.1: fetch nome agenzia disbrigo per la Card Passaggio
  useEffect(() => {
    const aid = v?.passaggio_proprieta?.agenzia_disbrigo_id;
    if (!aid) { setAgenziaPassName(null); return; }
    api.get(`/anagrafiche/${aid}`).then((r) => {
      const a = r.data;
      setAgenziaPassName(a.tipo_persona === "azienda"
        ? a.ragione_sociale
        : [a.cognome, a.nome].filter(Boolean).join(" "));
    }).catch(() => setAgenziaPassName(null));
  }, [v?.passaggio_proprieta?.agenzia_disbrigo_id]);

  // Sprint 4A.4 B3: permesso gestisci_impegno_contratto per vedere i bottoni workflow
  const canWorkflow = useMemo(() => haFlag(user, "gestisci_impegno_contratto", ambitoVendita.cid, ambitoVendita.sid),
    [user, ambitoVendita.cid, ambitoVendita.sid]);

  // Sprint 4Z A1: permesso differenziato per cambio cliente/cointestatario in
  // base allo status corrente della vendita.
  // - preventivo (qualsiasi status): gestisci_impegno_contratto
  // - pratica.impegnata: gestisci_impegno_contratto
  // - pratica.venduta: agire_su_pratica_venduta
  // - pratica.consegnata: agire_su_pratica_consegnata
  // - pratica.annullata: bloccato
  const anagraficaPermissionInfo = useMemo(() => {
    if (!v) return { allowed: false, reason: "" };
    if (isAdmin) return { allowed: true, reason: "" };
    const perms = user?.permissions_company_site || [];
    const has = (key) => perms.some((p) => p[key]);
    if (v.tipo === "preventivo") {
      if (has("gestisci_impegno_contratto")) return { allowed: true, reason: "" };
      return { allowed: false, reason: "Permesso richiesto: gestisci_impegno_contratto" };
    }
    if (v.status === "annullata") {
      return { allowed: false, reason: "Pratica annullata: archivio read-only" };
    }
    if (v.status === "consegnata") {
      if (has("agire_su_pratica_consegnata")) return { allowed: true, reason: "" };
      return { allowed: false, reason: "Permesso richiesto: agire_su_pratica_consegnata" };
    }
    if (v.status === "venduta") {
      if (has("agire_su_pratica_venduta")) return { allowed: true, reason: "" };
      return { allowed: false, reason: "Permesso richiesto: agire_su_pratica_venduta" };
    }
    if (has("gestisci_impegno_contratto")) return { allowed: true, reason: "" };
    return { allowed: false, reason: "Permesso richiesto: gestisci_impegno_contratto" };
  }, [v, user, isAdmin]);
  const canEditAnagrafica = anagraficaPermissionInfo.allowed;
  const anagraficaPermReason = anagraficaPermissionInfo.reason;

  // Sprint 4Z A1: stato modale edit cliente/cointestatario
  const [anagEditModal, setAnagEditModal] = useState(null); // { field, currentId }

  // Cache nomi (anagrafiche, users)
  const [anagraficheCache, setAnagraficheCache] = useState({});
  const [usersCache, setUsersCache] = useState({});
  const [allUsers, setAllUsers] = useState([]);

  const [pagamentoPopup, setPagamentoPopup] = useState({ open: false, initial: null });
  const [venditorePopup, setVenditorePopup] = useState(false);

  useEffect(() => {
    api.get("/users?page_size=300").then((r) => {
      const arr = r.data.items || r.data || [];
      setAllUsers(arr);
      setUsersCache(Object.fromEntries(arr.map((u) => [u.id, u])));
    }).catch(() => {});
  }, []);

  const load = () => {
    if (!venditaId) { setV(null); return; }
    api.get(`/vendite/${venditaId}`).then((r) => {
      setV(r.data);
      onLoaded?.(r.data);
      // Pre-fetch anagrafiche
      const ids = [r.data.cliente_id, r.data.cointestatario_vendita_id].filter(Boolean);
      ids.forEach((id) => {
        if (!anagraficheCache[id]) {
          api.get(`/anagrafiche/${id}`).then((rr) => {
            setAnagraficheCache((m) => ({ ...m, [id]: rr.data }));
          }).catch(() => {});
        }
      });
      if (r.data.vehicle_id) {
        api.get(`/vehicles/${r.data.vehicle_id}`).then((rv) => {
          setVehicle(rv.data);
          if (rv.data.supplier_id && !anagraficheCache[rv.data.supplier_id]) {
            api.get(`/anagrafiche/${rv.data.supplier_id}`).then((ra) => {
              setAnagraficheCache((m) => ({ ...m, [rv.data.supplier_id]: ra.data }));
            }).catch(() => {});
          }
          // Sprint 4I1.B2.C — fetch azienda per iva_codice_default
          if (rv.data.company_id) {
            api.get(`/companies/${rv.data.company_id}`).then((rc) => {
              setCompanyIvaDefault(rc.data?.iva_codice_default || "I22");
            }).catch(() => {});
          }
        }).catch(() => {});
      }
    });
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [venditaId]);

  if (!venditaId) return null;
  if (!v) return <div className="p-10 text-slate-400" data-testid="vendita-loading">Caricamento...</div>;

  const m = v.margine || {};
  const snap = v.vehicle_snapshot || {};
  const hidden = !!v.nascondi_importi_acquisto;
  const isClosed = ["venduta", "consegnata"].includes(v.status);
  // Sprint 4Z.1 F3: pratica annullata = archivio read-only.
  // Tutti gli input bloccati come per le chiuse, ma con l'opzione "Riapri".
  const isAnnullata = v.tipo === "pratica" && v.status === "annullata";
  // Sprint VEND-UNLOCK: per pratiche CONSEGNATE, anche se `canUnlock=true`,
  // serve lo sblocco temporaneo esplicito. Per VENDUTE, comportamento
  // invariato (canUnlock basta).
  const isConsegnata = v.status === "consegnata";
  const effectiveUnlock = isConsegnata
    ? (canUnlock && isTempUnlocked)
    : canUnlock;
  const isLocked = (isClosed && !effectiveUnlock) || isAnnullata;
  // Pagamenti restano modificabili da chi ha gestione_incassi (eccezione lock)
  const canEditPayments = canEconomici && (!isLocked || canEconomici) && !isAnnullata;

  // ----- pagamenti -----
  // Sprint VEND-UNLOCK: ogni mutazione riuscita ripristina il blocco
  // temporaneo. Usare `applyMutation` dopo qualunque setV(r.data) di
  // operazione mutativa (POST/PATCH/PUT/DELETE).
  const applyMutation = (data) => { setV(data); setIsTempUnlocked(false); };
  const savePagamento = async (data) => {
    try {
      const url = data.id
        ? `/vendite/${venditaId}/pagamenti/${data.id}`
        : `/vendite/${venditaId}/pagamenti`;
      const method = data.id ? "patch" : "post";
      const body = { ...data };
      if (!data.id) delete body.id;
      const r = await api[method](url, body);
      applyMutation(r.data);
      setPagamentoPopup({ open: false, initial: null });
      toast.success("Pagamento salvato");
    } catch (e) { toast.error(extractError(e)); }
  };
  const deletePagamento = async (pid) => {
    if (!window.confirm("Eliminare questo pagamento?")) return;
    try {
      const r = await api.delete(`/vendite/${venditaId}/pagamenti/${pid}`);
      applyMutation(r.data); toast.success("Pagamento rimosso");
    } catch (e) { toast.error(extractError(e)); }
  };

  // ----- venditore -----
  const saveVenditore = async (newId) => {
    try {
      const r = await api.patch(`/vendite/${venditaId}`, { venditore_id: newId });
      applyMutation(r.data); setVenditorePopup(false); toast.success("Venditore aggiornato");
    } catch (e) { toast.error(extractError(e)); }
  };

  // ----- actions -----
  const patchTop = async (payload) => {
    try {
      const r = await api.patch(`/vendite/${venditaId}`, payload);
      applyMutation(r.data); toast.success("Salvato");
    } catch (e) { toast.error(extractError(e)); }
  };

  const saveRiga = async (row) => {
    const cat = rigaPopup.categoria;
    try {
      const url = row.id
        ? `/vendite/${venditaId}/righe/${cat}/${row.id}`
        : `/vendite/${venditaId}/righe/${cat}`;
      const method = row.id ? "patch" : "post";
      const body = { ...row };
      if (!row.id) delete body.id;  // backend non vuole id su create
      const r = await api[method](url, body);
      applyMutation(r.data);
      setRigaPopup({ open: false, categoria: cat, initial: null });
      toast.success("Riga salvata");
    } catch (e) {
      console.error("[saveRiga] error", e?.response?.data || e);
      toast.error(extractError(e));
    }
  };

  const deleteRiga = async (cat, rid) => {
    if (!window.confirm("Eliminare questa riga?")) return;
    try {
      const r = await api.delete(`/vendite/${venditaId}/righe/${cat}/${rid}`);
      applyMutation(r.data); toast.success("Riga rimossa");
    } catch (e) { toast.error(extractError(e)); }
  };

  // Sprint PRATICHE-CONCORRENZA — le 4 voci salvate in blocco: punto di partenza congelato
  // all'apertura della finestra (riaprire lo rinnova), si mandano solo i campi cambiati.
  const congela = (voce, aperta, valore) => {
    if (!aperta) { delete aperte.current[voce]; return valore; }
    if (!aperte.current[voce]) aperte.current[voce] = { valore };
    return aperte.current[voce].valore;
  };
  const salvaVoce = async (rotta, voce, data) => {
    const { payload, originali } = campiModificati(aperte.current[voce]?.valore || {}, data);
    const r = await api.put(`/vendite/${venditaId}/${rotta}`, { ...payload, originali });
    dopoVoce(r.data, rotta, voce, payload);
    return r.data;
  };
  const dopoVoce = (data, rotta, voce, mio) => {
    applyMutation(data);
    if (data.conflitti?.length) setEsitoVoce({ esito: data, rotta, voce, mieiValori: { [voce]: mio } });
  };
  const applicaVoce = async (scelte) => {
    const { esito, rotta, voce, mieiValori } = esitoVoce;
    const miei = esito.conflitti.filter((c) => scelte[c.campo] === "mio");
    setEsitoVoce(null);
    if (!miei.length) { toast.success("Tenuti i valori attuali"); return; }
    try {
      const { payload, originali } = sovrascrittura(miei, mieiValori, `${voce}.`);
      const r = await api.put(`/vendite/${venditaId}/${rotta}`, { ...payload, originali });
      applyMutation(r.data);
      if (r.data.conflitti?.length) setEsitoVoce({ esito: r.data, rotta, voce, mieiValori });
      else toast.success(`Sovrascritt${miei.length === 1 ? "o 1 campo" : `i ${miei.length} campi`}`);
    } catch (e) { toast.error(extractError(e)); }
  };

  const saveFin = async (data) => {
    try {
      const d = await salvaVoce("finanziamento", "finanziamento", data);
      setFinPopup(false); if (!d.conflitti?.length) toast.success("Finanziamento salvato");
    } catch (e) { toast.error(extractError(e)); }
  };
  const deleteFin = async () => {
    if (!window.confirm("Rimuovere il finanziamento? (i TAEG/TAN torneranno modificabili)")) return;
    const r = await api.delete(`/vendite/${venditaId}/finanziamento`);
    applyMutation(r.data); toast.success("Finanziamento rimosso");
  };

  // Sprint 4F2.1: passaggio di proprietà
  const onPassSaved = (data, mio) => {
    dopoVoce(data, "passaggio", "passaggio_proprieta", mio);
  };
  const deletePass = async () => {
    if (!window.confirm("Rimuovere il passaggio di proprietà?")) return;
    const r = await api.delete(`/vendite/${venditaId}/passaggio`);
    applyMutation(r.data); toast.success("Passaggio rimosso");
  };

  const savePerm = async (data) => {
    const d = await salvaVoce("permuta", "permuta", data);
    setPermPopup(false); if (!d.conflitti?.length) toast.success("Permuta salvata");
  };
  const delPerm = async () => {
    if (!window.confirm("Rimuovere la permuta?")) return;
    const r = await api.delete(`/vendite/${venditaId}/permuta`);
    applyMutation(r.data);
  };

  const saveCap = async (data) => {
    const d = await salvaVoce("pagamento-caparra", "pagamenti_caparra", data);
    setCapPopup(false); if (!d.conflitti?.length) toast.success("Caparra salvata");
  };
  const delCap = async () => {
    if (!window.confirm("Rimuovere la caparra?")) return;
    const r = await api.delete(`/vendite/${venditaId}/pagamento-caparra`);
    applyMutation(r.data);
  };

  // ----- render -----
  // Sprint VEND-UNLOCK: ogni operazione di salvataggio riuscita ripristina il blocco.
  // Sprint PRATICHE-CONCORRENZA — sblocco e ri-blocco registrati; avviso = un FATTO, mai un blocco
  const sblocca = async () => {
    setIsTempUnlocked(true);
    try {
      const r = await api.post(`/vendite/${venditaId}/sblocco-temporaneo`, { attivo: true });
      if (r.data?.ultimo_sblocco) toast.warning(r.data.ultimo_sblocco.messaggio, { duration: 10000 });
    } catch { /* lo sblocco resta: la registrazione non deve impedirlo */ }
  };
  const reLock = () => {
    setIsTempUnlocked(false);
    api.post(`/vendite/${venditaId}/sblocco-temporaneo`, { attivo: false }).catch(() => {});
  };
  // canUnlock effettivo per i pulsanti di transizione: su CONSEGNATA serve
  // ANCHE lo sblocco temporaneo attivo (regola b1 dello sprint VEND-UNLOCK).
  const workflowUnlock = isConsegnata ? (canUnlock && isTempUnlocked) : canUnlock;
  return (
    <div className="space-y-4 pb-16" data-testid="vendita-detail">
      <LockBanner
        vendita={v}
        canUnlock={canUnlock}
        isTempUnlocked={isTempUnlocked}
        onTempUnlock={sblocca}
        onTempLock={reLock}
      />
      {/* Sprint 4Z.1 F3 — Banner pratica annullata + bottone Riapri */}
      {isAnnullata && (
        <AnnullataBanner vendita={v} canReopen={canWorkflow || isAdmin} onReopened={load} />
      )}
      {/* HEADER */}
      {!hideHeader && (
        <Card className="p-4 flex flex-wrap items-start justify-between gap-4">
          <div className="flex-1 min-w-[280px]">
            <div className="text-[11px] uppercase tracking-wider text-slate-500">{v.tipo === "preventivo" ? "Preventivo" : "Pratica di vendita"}</div>
            <div className="font-heading text-3xl text-slate-900 flex items-center gap-3 flex-wrap">
              {/* Sprint 4Z.2 E1 — TipoCaricoBadge a fianco del codice */}
              {(vehicle?.tipo_carico || v.vehicle_tipo_carico) && (
                <TipoCaricoBadge value={vehicle?.tipo_carico || v.vehicle_tipo_carico} />
              )}
              {v.codice}
              {/* Sprint 4X.2: badge 2-assi (doppio se VIRTUAL/INCOMING + impegnata/venduta) */}
              <VehicleStatusBadge
                disponibilita_fisica={vehicle?.disponibilita_fisica || v.vehicle_disponibilita_fisica}
                stato_commerciale={v.status}
              />
              {/* Sprint 4X.1: WorkflowFlowBar visibile anche su pagina stand-alone */}
              <WorkflowFlowBar
                vendita={v}
                vehicle={vehicle}
                margine={m}
                isAdmin={isAdmin}
                canWorkflow={canWorkflow}
                canUnlock={workflowUnlock}
                onDone={load}
              />
              {v.tipo === "preventivo" && (
                <PreventivoActionsPanel vendita={v} isAdmin={isAdmin} onDone={load} compact
                  onScegliCliente={() => setAnagEditModal({ field: "cliente_id", currentId: null })} />
              )}
            </div>
            <HeaderBadges
              vendita={v} vehicle={vehicle}
              anagraficheCache={anagraficheCache} usersCache={usersCache}
              canEditVenditore={isAdmin}
              onEditVenditore={() => setVenditorePopup(true)}
              canEditAnagrafica={canEditAnagrafica}
              anagraficaPermReason={anagraficaPermReason}
              onEditAnagrafica={(field, currentId) => setAnagEditModal({ field, currentId })}
              onQualitaChanged={load}
              qualitaLocked={isLocked} qualitaSblocco={isConsegnata && effectiveUnlock}
            />
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-2 px-3 py-1.5 border rounded-md bg-slate-50">
              <Switch
                checked={hidden}
                onCheckedChange={(val) => patchTop({ nascondi_importi_acquisto: val })}
                disabled={isLocked}
                data-testid="toggle-nascondi-globale"
              />
              <span className="text-[12px] text-slate-700 flex items-center gap-1">
                {hidden ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                Modalità monitor cliente
              </span>
            </div>
            {vehicle && (
              <Button variant="outline" size="sm" onClick={() => setCambioOpen(true)} data-testid="btn-cambio-disp">
                <Car className="h-4 w-4 mr-1.5" /> Cambia disponibilità
              </Button>
            )}
          </div>
        </Card>
      )}
      {hideHeader && (
        <div className="flex items-start justify-between flex-wrap gap-3 pb-3 border-b border-slate-200">
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-heading text-xl text-slate-900">{v.codice}</span>
              <VehicleStatusBadge
                disponibilita_fisica={vehicle?.disponibilita_fisica || v.vehicle_disponibilita_fisica}
                stato_commerciale={v.status}
                size="sm"
              />
              {/* Sprint 4B.1 (A2): pulsanti regredisci/avanza in barra superiore */}
              <WorkflowFlowBar
                vendita={v}
                vehicle={vehicle}
                margine={m}
                isAdmin={isAdmin}
                canWorkflow={canWorkflow}
                canUnlock={workflowUnlock}
                onDone={load}
              />
              {/* Sprint 4X B-FIX-1: pulsanti preventivo (Conferma/Duplica/Riapri) nella barra superiore */}
              {v.tipo === "preventivo" && (
                <PreventivoActionsPanel vendita={v} isAdmin={isAdmin} onDone={load} compact
                  onScegliCliente={() => setAnagEditModal({ field: "cliente_id", currentId: null })} />
              )}
            </div>
            <HeaderBadges
              vendita={v} vehicle={vehicle}
              anagraficheCache={anagraficheCache} usersCache={usersCache}
              canEditVenditore={isAdmin}
              onEditVenditore={() => setVenditorePopup(true)}
              canEditAnagrafica={canEditAnagrafica}
              anagraficaPermReason={anagraficaPermReason}
              onEditAnagrafica={(field, currentId) => setAnagEditModal({ field, currentId })}
              onQualitaChanged={load}
              qualitaLocked={isLocked} qualitaSblocco={isConsegnata && effectiveUnlock}
            />
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 border rounded-md bg-slate-50">
            <Switch
              checked={hidden}
              onCheckedChange={(val) => patchTop({ nascondi_importi_acquisto: val })}
              disabled={isLocked}
              data-testid="toggle-nascondi-globale"
            />
            <span className="text-[12px] text-slate-700 flex items-center gap-1">
              {hidden ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
              Monitor cliente
            </span>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* COLONNA SINISTRA */}
        <div className="lg:col-span-2">
          {v.tipo === "pratica" && (
            <DatePraticaCard v={v} setV={setV} patchTop={patchTop} isLocked={isLocked} canUnlock={canUnlock} />
          )}
          <BandaSezione titolo="Importi del veicolo" />
          {/* Sprint 4A.3: Veicolo snapshot — etichette nuove + calcoli corretti */}
          <Card className="p-3">
            <div className="flex items-center gap-2 mb-2">
              <Car className="h-4 w-4 text-slate-700" />
              <h3 className="font-heading text-base font-semibold text-slate-900 uppercase tracking-wide">Veicolo</h3>
            </div>
            <PrezzoVenditaSelector
              vendita={v}
              snap={snap}
              margine={m}
              canEdit={canEconomici}
              isLocked={isLocked}
              onChangeScelta={(scelta) => patchTop({ prezzo_vendita_scelta: scelta })}
            />
            <table className="w-full text-sm mt-2">
              <thead className="text-[11px] uppercase text-slate-500 border-b">
                <tr>
                  <th className="text-left py-1">Voce</th>
                  <th className="text-right py-1 text-amber-700 w-[160px]">Acquisto</th>
                  <th className="text-right py-1 text-blue-700 w-[200px]">Vendita</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                <tr>
                  <td className="py-1.5">Veicolo + Spese</td>
                  <td className="py-1.5 text-right font-mono text-amber-700 border-l-2 border-amber-200 bg-amber-50/40 px-2" data-testid="snap-veicolo-piu-spese">
                    {formatEURMasked(m.costi_veicolo_piu_spese)}
                  </td>
                  <td className="py-1.5 px-1 bg-blue-50/30">
                    {canEconomici && (v.prezzo_vendita_scelta || "web") === "manuale" ? (
                      <Input
                        type="number" step="0.01"
                        value={v.prezzo_vendita_veicolo ?? ""}
                        onChange={(e) => setV({ ...v, prezzo_vendita_veicolo: e.target.value })}
                        onBlur={() => patchTop({ prezzo_vendita_veicolo: parseFloat(v.prezzo_vendita_veicolo || 0) || 0, prezzo_vendita_scelta: "manuale" })}
                        className="text-right font-mono border-l-4 border-blue-400 h-8"
                        data-testid="prezzo-vendita-veicolo"
                      />
                    ) : (
                      <div className="text-right font-mono text-blue-700 border-l-2 border-blue-200 py-1 pr-2" data-testid="prezzo-vendita-veicolo-readonly">
                        {formatEUR(m.prezzo_vendita_veicolo_effettivo)}
                      </div>
                    )}
                  </td>
                </tr>
                <tr>
                  <td className="py-1.5 text-slate-600">Approntamento</td>
                  <td className="py-1.5 text-right font-mono text-amber-700 border-l-2 border-amber-200 bg-amber-50/40 px-2" data-testid="snap-approntamento">
                    {formatEURMasked(m.costi_approntamento)}
                  </td>
                  <td className="py-1.5 px-1 bg-blue-50/30">
                    {canEconomici ? (
                      <Input
                        type="number" step="0.01"
                        value={v.prezzo_vendita_approntamento ?? ""}
                        onChange={(e) => setV({ ...v, prezzo_vendita_approntamento: e.target.value })}
                        onBlur={() => patchTop({ prezzo_vendita_approntamento: parseFloat(v.prezzo_vendita_approntamento || 0) || 0 })}
                        disabled={isLocked}
                        className="text-right font-mono border-l-4 border-blue-400 h-8"
                        data-testid="prezzo-vendita-approntamento"
                      />
                    ) : (
                      <div className="text-right font-mono text-blue-700 border-l-2 border-blue-200 py-1 pr-2">
                        {formatEUR(v.prezzo_vendita_approntamento || 0)}
                      </div>
                    )}
                  </td>
                </tr>
              </tbody>
            </table>
          </Card>

          <div className="mt-3 space-y-3">
            <SezioneRighe
              titolo="Servizi aggiuntivi" icon={CreditCard} categoria="servizi"
              righe={v.righe_servizi || []}
              onAdd={() => setRigaPopup({ open: true, categoria: "servizi", initial: { nascondi_acquisto: false, dettaglio: {} } })}
              onEdit={(r) => setRigaPopup({ open: true, categoria: "servizi", initial: r })}
              onDelete={(r) => deleteRiga("servizi", r.id)}
              color="text-blue-600"
            />
            <SezioneRighe
              titolo="Accessori" icon={Plus} categoria="accessori"
              righe={v.righe_accessori || []}
              onAdd={() => setRigaPopup({ open: true, categoria: "accessori", initial: { nascondi_acquisto: false, dettaglio: {} } })}
              onEdit={(r) => setRigaPopup({ open: true, categoria: "accessori", initial: r })}
              onDelete={(r) => deleteRiga("accessori", r.id)}
              color="text-indigo-600"
            />
            {/* Sprint 4F1 — Garanzie accessorie (sotto Accessori, dentro IMPORTI DEL VEICOLO) */}
            <GaranzieSection
              vendita={v}
              canEdit={!isLocked && canEconomici}
              onChange={load}
              aziendaIvaDefault={companyIvaDefault}
            />
            {/* Sprint 4F2.4 — Card Passaggio: pixel-pari con GaranzieSection. Pill rimosso. */}
            <CardCollapsible
              titolo="Passaggio di proprietà"
              icon={FileSignature}
              iconColor="text-amber-700"
              populated={!!v.passaggio_proprieta}
              summary={v.passaggio_proprieta
                ? (v.passaggio_proprieta.totale_acquisto_calcolato != null
                    ? `${formatEUR(v.passaggio_proprieta.totale_acquisto_calcolato)} acquisto · ${formatEUR(v.passaggio_proprieta.prezzo_vendita_cliente)} vendita`
                    : `${formatEUR(v.passaggio_proprieta.prezzo_vendita_cliente)} vendita`)
                : null}
              actions={!v.passaggio_proprieta && (
                <Button
                  size="sm" variant="outline"
                  onClick={() => setPassPopup(true)}
                  disabled={isLocked || !canEconomici}
                  data-testid="btn-pass-popup"
                >
                  <Plus className="h-4 w-4 mr-1" /> Aggiungi
                </Button>
              )}
              data-testid="card-passaggio"
            >
              <PassaggioProprietaView
                vendita={v}
                agenziaName={agenziaPassName}
                canEdit={!isLocked && canEconomici}
                onEdit={() => setPassPopup(true)}
                onDelete={deletePass}
              />
            </CardCollapsible>
            {/* Sprint 4F2-CVT.C — Card Assicurazione CVT (esterna) */}
            <PolizzaCVTSection
              vendita={v}
              canEdit={!isLocked && canEconomici}
              onChange={load}
            />
            <SezioneRighe
              titolo="Sconti" icon={ArrowLeftRight} categoria="sconti"
              righe={v.righe_sconti || []}
              onAdd={() => setRigaPopup({ open: true, categoria: "sconti", initial: { dettaglio: {} } })}
              onEdit={(r) => setRigaPopup({ open: true, categoria: "sconti", initial: r })}
              onDelete={(r) => deleteRiga("sconti", r.id)}
              color="text-rose-600"
            />
          </div>

          <BandaSezione titolo="Pagamenti del veicolo" />
          <CardCollapsible
            titolo="Finanziamento"
            titleExtra={(v.finanziamento?.totale_proventi || 0) > 0 && (
              <span
                className="text-[11px] font-medium px-2 py-0.5 rounded-full ml-2 border bg-emerald-100 text-emerald-600 border-emerald-200"
                data-testid="fin-proventi-pill"
              >
                +{formatEUR(v.finanziamento.totale_proventi)} proventi
              </span>
            )}
            icon={Landmark}
            iconColor="text-purple-600"
            populated={!!v.finanziamento}
            summary={v.finanziamento ? `${formatEUR(v.finanziamento.importo_finanziato)} · ${(v.finanziamento.rate_periodo_1?.numero_rate || 0) + (v.finanziamento.rate_periodo_2?.numero_rate || 0)} mesi` : null}
            actions={
              <div className="flex gap-2">
                {v.finanziamento && (
                  <Button size="sm" variant="outline" onClick={deleteFin} className="text-rose-600">
                    <Trash2 className="h-4 w-4 mr-1" /> Rimuovi
                  </Button>
                )}
                <Button size="sm" variant="outline" onClick={() => setFinPopup(true)} data-testid="btn-fin-popup">
                  {v.finanziamento ? <><Pencil className="h-4 w-4 mr-1" /> Modifica</> : <><Plus className="h-4 w-4 mr-1" /> Aggiungi</>}
                </Button>
              </div>
            }
            data-testid="card-finanziamento"
          >
            {v.finanziamento ? (
              <FinanziamentoView vendita={v} providerName={finanziariaName} />
            ) : (
              <div className="text-sm text-slate-400 italic">Nessun finanziamento</div>
            )}
          </CardCollapsible>

          <div className="mt-3">
          <CardCollapsible
            titolo="Permuta"
            icon={ArrowLeftRight}
            iconColor="text-slate-700"
            populated={!!v.permuta}
            summary={v.permuta ? `${v.permuta.veicolo_descrizione || ""} · ${formatEUR(v.permuta.valore_ritiro)}` : null}
            actions={
              <div className="flex gap-2">
                {v.permuta && (
                  <Button size="sm" variant="outline" onClick={delPerm} className="text-rose-600">
                    <Trash2 className="h-4 w-4 mr-1" /> Rimuovi
                  </Button>
                )}
                <Button size="sm" variant="outline" onClick={() => setPermPopup(true)}>
                  {v.permuta ? <><Pencil className="h-4 w-4 mr-1" /> Modifica</> : <><Plus className="h-4 w-4 mr-1" /> Aggiungi</>}
                </Button>
              </div>
            }
            data-testid="card-permuta"
          >
            {v.permuta ? (
              <div className="space-y-2 text-sm">
                {/* Sprint PERMUTA-UX C.1 — Riga intestazione MARCA | MODELLO | VERSIONE | TARGA con valori sotto */}
                <div className="grid grid-cols-4 gap-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500">Marca</div>
                    <div className="font-medium text-slate-900">{v.permuta.marca || "—"}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500">Modello</div>
                    <div className="font-medium text-slate-900">{v.permuta.modello || "—"}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500">Versione</div>
                    <div className="font-medium text-slate-900">{v.permuta.versione || "—"}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500">Targa</div>
                    <div className="font-mono text-slate-900">{v.permuta.targa || "—"}</div>
                  </div>
                </div>
                {/* Valore ritiro a destra + badge materializzata */}
                <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                  <div className="flex items-center gap-3">
                    {v.permuta.vehicle_id && (
                      <span className="text-[11px] uppercase text-emerald-600 font-semibold"
                            data-testid="permuta-materializzata-badge">
                        ✓ Materializzata
                      </span>
                    )}
                    {v.permuta.km != null && (
                      <span className="text-xs text-slate-500">{v.permuta.km} km</span>
                    )}
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500">Valore ritiro</div>
                    <div className="font-mono font-semibold text-slate-900">{formatEUR(v.permuta.valore_ritiro)}</div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-sm text-slate-400 italic">Nessuna permuta</div>
            )}
          </CardCollapsible>
          </div>

          {/* Sprint 4B: pagamenti nascosti sui preventivi */}
          {v.tipo !== "preventivo" && (
            <SezionePagamenti
              pagamenti={v.pagamenti || []}
              margine={m}
              onAdd={() => setPagamentoPopup({ open: true, initial: { tipo: "acconto", modalita: "bonifico" } })}
              onEdit={(p) => setPagamentoPopup({ open: true, initial: p })}
              onDelete={(p) => deletePagamento(p.id)}
              readOnly={!canEditPayments}
            />
          )}
          {v.tipo === "preventivo" && (
            <PreventivoActionsPanel vendita={v} isAdmin={isAdmin} onDone={load} />
          )}

          {v.tipo === "pratica" && (
            <DatiConsegnaCard v={v} patchTop={patchTop} disabled={isLocked} />
          )}

          <BandaSezione titolo="Note" />
          <Card className="p-3" data-testid="card-note">
            <div className="flex items-center gap-2 mb-2">
              <StickyNote className="h-4 w-4 text-slate-700" />
              <h3 className="font-heading text-base font-semibold text-slate-900">Note</h3>
            </div>
            <NoteTabs v={v} setV={setV} patchTop={patchTop} />
          </Card>
        </div>

        {/* COLONNA DESTRA — riepilogo unificato + residuo (Sprint 4B.2 B8) */}
        <div className="space-y-3 lg:sticky lg:top-4 self-start" data-testid="riepilogo-sticky">
          {/* Sprint UI-PREV — semaforo margine spostato sopra al Riepilogo.
              Sprint FIX-MONITOR2: il box resta visibile sempre; in Monitor
              Cliente solo i NUMERI (importo+percentuale) vengono nascosti. */}
          <RegimeIvaRow v={v} onGoTo={onGoTo} canUnlock={canUnlock} isTempUnlocked={isTempUnlocked} onAllineato={load} />
          <CvtCoerenzaAlert margine={m} />
          <div className="px-3 py-2 bg-white border rounded-md" data-testid="margine-sticky-top">
            <MargineInline margine={m} nascosto={hidden} canSeeNumbers={canEconomici} onGoTo={onGoTo} />
          </div>
          <Card className="p-3">
            <h3 className="font-heading text-base font-semibold mb-2">Riepilogo</h3>
            <div className="space-y-1 text-sm">
              <div className="text-[10px] uppercase tracking-wide text-slate-500">Importi</div>
              <div className="flex justify-between"><span>Prezzo veicolo</span><span className="font-mono text-slate-900">{formatEUR(m.prezzo_vendita_veicolo_effettivo)}</span></div>
              {/* Sprint 4F1.3: Approntamento dopo Prezzo veicolo */}
              {(m.prezzo_vendita_approntamento || 0) > 0 && (
                <div className="flex justify-between text-slate-600" data-testid="riep-approntamento">
                  <span>Approntamento</span>
                  <span className="font-mono">{formatEUR(m.prezzo_vendita_approntamento)}</span>
                </div>
              )}
              <div className="flex justify-between text-slate-600"><span>Servizi</span><span className="font-mono" data-testid="riep-servizi">{formatEUR(m.totale_vendite_servizi)}</span></div>
              <div className="flex justify-between text-slate-600"><span>Accessori</span><span className="font-mono" data-testid="riep-accessori">{formatEUR(m.totale_vendite_accessori)}</span></div>
              {(v.righe_garanzie?.length || 0) > 0 && (
                <div className="flex justify-between text-emerald-700" data-testid="riep-garanzie">
                  <span>Garanzie</span>
                  <span className="font-mono">+{formatEUR(m.totale_vendite_garanzie)}</span>
                </div>
              )}
              {v.passaggio_proprieta && (
                <div className="flex justify-between text-amber-700" data-testid="riep-passaggio">
                  <span>Passaggio di proprietà</span>
                  <span className="font-mono">+{formatEUR(m.passaggio_vendita)}</span>
                </div>
              )}
              {!!m.cvt_totale_cliente && (
                <>
                  <div className="flex justify-between text-indigo-700" data-testid="riep-cvt">
                    <span>Assicurazione CVT</span>
                    <span className="font-mono">+{formatEUR(m.cvt_premio)}</span>
                  </div>
                  {(m.cvt_servizi_extra || []).map((x, i) => (
                    <div key={i} className="flex justify-between text-indigo-600 text-xs pl-4" data-testid={`riep-cvt-extra-${i}`}>
                      <span>{x.descrizione}</span>
                      <span className="font-mono">+{formatEUR(x.importo)}</span>
                    </div>
                  ))}
                </>
              )}
              <div className="flex justify-between text-rose-700"><span>Sconti</span><span className="font-mono">{formatEUR(m.totale_sconti)}</span></div>
              <div className="h-px bg-slate-200 my-1.5" />
              <div className="flex justify-between font-heading text-base text-blue-700 uppercase tracking-wide">
                <span className="font-bold">Totale vendita</span>
                <span className="font-mono font-bold" data-testid="tot-vendita">{formatEUR(m.totale_vendita_finale)}</span>
              </div>

              {v.tipo !== "preventivo" && (
                <>
                  <div className="h-px bg-slate-200 my-2" />
                  <div className="text-[10px] uppercase tracking-wide text-slate-500">Pagamenti</div>
                  <div className="flex justify-between items-center">
                    <span className="flex items-center gap-1.5">
                      Finanziamento
                      {v.finanziamento && !m.finanziamento_conta_in_pagamenti && (
                        <span title="Importo non conta nei pagamenti finché lo stato finanziamento non è 'erogato'"
                          className="text-[10px] uppercase bg-slate-200 text-slate-600 px-1.5 py-0 rounded"
                          data-testid="fin-not-erogato">
                          in attesa
                        </span>
                      )}
                    </span>
                    <span className={`font-mono ${m.finanziamento_conta_in_pagamenti ? "text-emerald-700" : "text-slate-400"}`}>
                      {v.finanziamento ? formatEUR(v.finanziamento.importo_finanziato) : "—"}
                    </span>
                  </div>
                  <div className="flex justify-between"><span>Permuta</span><span className="font-mono text-right">{formatEUR(v.permuta?.valore_ritiro)}</span></div>
                  <div className="flex justify-between"><span>Caparra</span><span className="font-mono text-right" data-testid="riep-pag-caparra">{formatEUR(m.totale_pagamenti_caparra)}</span></div>
                  <div className="flex justify-between"><span>Acconti</span><span className="font-mono text-right" data-testid="riep-pag-acconti">{formatEUR(m.totale_pagamenti_acconto)}</span></div>
                  <div className="flex justify-between"><span>Saldo</span><span className="font-mono text-right" data-testid="riep-pag-saldo">{formatEUR(m.totale_pagamenti_saldo)}</span></div>
                  <div className="h-px bg-slate-200 my-1.5" />
                  <div className="flex justify-between font-heading text-base uppercase tracking-wide">
                    <span className="font-bold">Totale pagamenti</span>
                    <span className="font-mono text-right font-bold" data-testid="riep-pag-totale">{formatEUR(m.totale_pagamenti)}</span>
                  </div>
                </>
              )}
            </div>
          </Card>

          {/* Residuo / Saldo / Eccesso (sfondo colorato) */}
          {v.tipo !== "preventivo" && (
            <ResiduoBox margine={m} />
          )}
        </div>
      </div>

      {/* Popups */}
      <RigaPopup
        open={rigaPopup.open}
        onOpenChange={(o) => setRigaPopup({ ...rigaPopup, open: o })}
        categoria={rigaPopup.categoria}
        initial={rigaPopup.initial}
        onSave={saveRiga}
        righeCollegabili={[
          ...((v.righe_servizi || []).map((r) => ({ ...r, _cat: "servizi" }))),
          ...((v.righe_accessori || []).map((r) => ({ ...r, _cat: "accessori" }))),
        ]}
        aziendaIvaDefault={companyIvaDefault}
        contextCompanyId={v?.company_id || null}
      />
      <FinanziamentoPopup open={finPopup} onOpenChange={setFinPopup} initial={congela("finanziamento", finPopup, v.finanziamento)} onSave={saveFin}
                          contextCompanyId={v?.company_id || null} />
      <PassaggioProprietaPopup
        open={passPopup}
        onOpenChange={setPassPopup}
        vendita={{ ...v, passaggio_proprieta: congela("passaggio_proprieta", passPopup, v.passaggio_proprieta) }}
        partenza={aperte.current.passaggio_proprieta?.valore}
        onSaved={onPassSaved}
      />
      <PermutaPopup open={permPopup} onOpenChange={setPermPopup} initial={congela("permuta", permPopup, v.permuta)} onSave={savePerm}
        onAgganciaVehicle={(setFields) => {
          // Sprint PERMUTA-UX (PARTE A.2) — picker veicolo vero al posto di window.prompt.
          // Il callback viene salvato in state e invocato dal VeicoloPicker dopo la scelta.
          setPermVehiclePickerCallback(() => (veh) => {
            setFields(veh.id, veh);
            toast.success(`Veicolo ${veh.codice || veh.id} agganciato`);
            setPermVehiclePickerOpen(false);
          });
          setPermVehiclePickerOpen(true);
        }}
      />
      <VeicoloPicker
        open={permVehiclePickerOpen}
        onOpenChange={setPermVehiclePickerOpen}
        onSelect={(veh) => { permVehiclePickerCallback && permVehiclePickerCallback(veh); }}
        excludeId={v.vehicle_id || null}
        contextCompanyId={v.company_id || null}
        testIdPrefix="perm-veicolo-picker"
      />
      <CaparraPopup open={capPopup} onOpenChange={setCapPopup} initial={congela("pagamenti_caparra", capPopup, v.pagamenti_caparra)} onSave={saveCap} />
      <Conflitti esito={esitoVoce?.esito} mieiValori={esitoVoce?.mieiValori} onApplica={applicaVoce}
        onChiudi={() => { setEsitoVoce(null); toast.message("Tenuti i valori attuali sui campi in conflitto"); }} />
      <PagamentoPopup
        open={pagamentoPopup.open}
        onOpenChange={(o) => setPagamentoPopup({ ...pagamentoPopup, open: o })}
        initial={pagamentoPopup.initial}
        onSave={savePagamento}
      />
      <VenditorePopup
        open={venditorePopup}
        onOpenChange={setVenditorePopup}
        initial={v.venditore_id}
        users={allUsers}
        onSave={saveVenditore}
      />
      <CambioDisponibilitaModal
        open={cambioOpen}
        onOpenChange={setCambioOpen}
        vehicle={vehicle}
        onUpdated={() => load()}
      />
      <EditAnagraficaModal
        open={!!anagEditModal}
        onOpenChange={(o) => !o && setAnagEditModal(null)}
        vendita={v}
        field={anagEditModal?.field}
        currentId={anagEditModal?.currentId}
        onSaved={() => { setAnagEditModal(null); load(); }}
      />
    </div>
  );
}

export { EmptyVendita };
